// ============================================================================
// IMPORTS SECTION
// ============================================================================
// React core
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
// Routing
import { Link, useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
// Form management
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
// Google Maps integration
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { getAuthCookie } from "../../utils/authCookies";
import { studioBatchVariantPreviewsFromCartLines } from "../../utils/signageStudioBatchRestore";
import {
  buildCartHeaderFromCart,
  buildPerItemCouponMapFromCartItems,
  getInitialShippingSelectionFromOrder,
  getInitialSpecialFeeIdsFromOrder,
  getLineItemsFromCart,
  getOrderIdFromResponse,
  getOrderNumberFromResponse,
  getResumeOrderIdFromCart,
  getResumeOrderNumberFromCart,
  linkCartToDraftOrder,
  getShippingRowsFromOrder,
  getSpecialFeeRowsFromOrder,
  mapOrderShippingRowsForPicker,
  type InitialShippingSelection,
} from "../../utils/checkoutCartOrderLink";
// Stripe payment integration
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentForm from "../../components/PaymentForm";
// UI Components - Select/Dropdown
import Select from "react-select";
import { SingleValue } from "react-select";
// Redux state management
// Context providers
// Type definitions
import { TaxRate, TaxRule, TaxGroup, Order, Product, License, Group, PaymentMethod, Address, Addresses, PaymentGateway, ShippingCost } from "types";
// Custom components
import PaymentMethods from "components/PaymentMethods";
import ProductPickerModal from "components/ordercheckoutpop";
import AddressFormModal from "components/AddAddressFormModal";
import { UIReactSelectPro, UIInputPro } from "components/OrdersSelectInput";
import Instructions from "components/Instructions";
import PaymentPanel from "components/PaymentOptionsCard";
import PaymentFormManualOrder from "components/PaymentFormManualOrder";
import { MobileCardsOnly } from "components/TableandMobilecomponent";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import InstructionsCreateCard from "components/Notes";
// Tax & Shipping components
import ShippingOptions, { type ShippingOptionsV2Handle } from "components/Tax/ShippingOptionsui";
import PONumber from "components/PONumber";
import SpecialFeePicker, { type SpecialFeePickerV2Handle } from "components/Tax/SpecialFeeui";
import { useCouponExpirationCheck } from "components/Tax/CouponExpirationCheck";
// Table components
import { useReactTable, getCoreRowModel, createColumnHelper, type ColumnDef } from "@tanstack/react-table";
// Assets
import ProductSelection from "assets/images/brands/ProductSelection.png";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import OrderNotes from "assets/images/brands/OrderNotes.png";
// Utilities
import { showSuccess, showError } from "toast/toast";
import { useAppDispatch } from "../../redux/hooks";
import { clearCart } from "../../redux/slices/cartSlice";
import { useCart } from "components/CartContext";
import { CheckoutPaymentModal, clearCheckoutCartAfterPayment } from "components/checkout";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { getSalesResolveDiscountCode } from "utils/salesResolveDiscountCode";
import { formatShippingChargeOrderTotalsLabel } from "utils/orderTotalsChargeLabels";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { DiscountAmountDisplay, getAgentRealtorCheckoutLinePricing } from "components/DiscountLabelBadge";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  postSalesOrderResolve,
  buildSalesResolvePayload,
  applySalesResolveResponse,
  overlaySalesSubmitFromResolved,
  mapCheckoutCartToResolveCatalogRows,
  mergeCheckoutCartFromResolved,
  perItemCouponMapFromResolved,
  pickAgentCheckoutResolveBuyer,
  getCheckoutOrderChargeFallbacks,
  readCheckoutCartLineTaxable,
  getCheckoutLineItemId,
  finalizeSalesOrderTaxAfterPaid,
  fetchSalesOrderForPaidFinalize,
  type ResolvedSalesOrder,
} from "orderResolve";
import {
  resolveShippingTaxableForPayload,
  resolveSpecialFeeTaxableForPayload,
  resolveSpecialFeeRequiredForPayload,
} from "orderResolve/utils/readResolveTaxable";


// ============================================================================
// TYPE DEFINITIONS SECTION
// ============================================================================
// Product object structure for API responses
type ProductObj = {
  product_id: string | number;
  product: {
    variants?: Array<{ variant_id: string | number; package_dimensions?: any }>;
    package_dimensions?: any;
  };
};

// Address form values for billing/shipping address inputs
interface AddressFormValues {
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  county: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
}

// Package dimensions for shipping calculations
type PackageDimensions = {
  length: number;
  width: number;
  height: number;
  weight: number;
  weight_type?: string; // Weight unit type (e.g., "lbs", "kg")
};

// Line item tax information structure
type LineTax = {
  tax_percentage: number;
  unit_product_tax_amount: number;
  total_product_tax_amount: number;
  taxable: boolean;
  tax_details?: {
    combined_rate?: number;
    tax_amount?: number;
    applied_state?: string;
  };
};

// Flexible line tax type - can be single, array, or null/undefined
type MaybeLineTax = LineTax | LineTax[] | null | undefined;


// Cart item structure - represents a product variant in the shopping cart
interface CartItem {
  product_id: string;
  variant_id: string;
  warehouse_id: string;
  name: string;
  product_name: string;
  sku: string;
  changed_unit_price?: number; // Price after discounts
  unit_purchase_price: number;
  /** Per-line discount allocated per unit (API / cart line) */
  discount_on_unit_quantity?: number;
  discount_on_total_quantity?: number;
  unit_retail_price: number;
  quantity: number;
  is_taxable?: boolean;
  is_production?: boolean;
  tax_details: {
    tax_rate: number;
    tax_amount: number;
    applied_state: string;
  };
  package_dimensions?: PackageDimensions | null;
  image_url?: string;
  images: any[];
  amount: number;
  tax_rule: TaxRule;
  tax_group: TaxGroup;
  customizations?: {
    template_name: string;
    fields: {
      field_name: string;
      field_type: "text" | "file";
      required: boolean;
      value?: string;
    }[];
  },
  design_id?: string;
  design_name?: string;
  preview_url?: string;
  item_tax_info: MaybeLineTax;
  storage_location: {
    bin_id: string;
    bin: string;
    rack_id: string;
    rack: string;
    warehouse_id: string;
    warehouse: string;
    zone_id: string;
    zone: string;
  }
}

// Form initial values for Formik - all form fields with default values
type InitialValues = {
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  userPhone: string;
  agent_id: string;
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  city: string;
  county: string;
  state: string;
  zipcode: string;
  phone: string;
  billingAddressId: string;
  shippingAddressId: string;
  shippingMethod: string; // "free" | etc
  isSameAsBilling: boolean;
  notes: string;
  po_number: string;
};

// UPS shipping option UI representation
type UpsUIOption = {
  id: string;
  code: string;        // UPS service code
  service: string;     // Display name (e.g., "UPS Ground")
  days: string;        // Delivery estimate (e.g., "3 business days")
  cost: number;        // Shipping cost
  subtitle: string;    // Additional info (e.g., "Guaranteed delivery")
};

// Shipping payload types from child components
type ShipSelectedIds = {
  manual_shipping_id: string | null;
  ups_service_code: string | null;
  integration_id: string | null;
  shipper_address_id: string | null;
};

// Shipping payload structure from ShippingOptions component
type ShipPayload = {
  shipping_option?: Array<{
    shipping__id?: string;
    id?: string;
    shipping_amount?: number;
    shipping_tax_percentage?: number;
    shipping_taxable?: boolean;
    shipping_delivarydate?: string;     // Note: keeping both spellings for compatibility
    shipping_tax_amount?: number;
  }> | null;
  isUps?: boolean;
  isManual?: boolean;
  selected_ids?: ShipSelectedIds;
  ship_to?: any;
  packages?: any[];
};

function toCheckoutChargeShipPayload(
  payload: ShipPayload | { shipping_option?: unknown[] | null } | null | undefined
): { shipping_option?: unknown[] } | null | undefined {
  if (payload == null) return payload;
  const rows = payload.shipping_option;
  return { shipping_option: rows == null ? undefined : rows };
}

// Special fee row structure
type FeeRow = {
  specialfee_id: string;
  specialfee_name?: string;
  specialfee_amount: number;
  specialfee_tax_amount: number;
  specialfee_tax_percentage?: number;
  specialfee_taxable?: boolean;
  required?: boolean;
};

// Special fee payload from SpecialFeePicker component
type FeePayload = {
  specialfee?: FeeRow[];
  selected_fee_ids?: string[];
  total_amount?: number;
  total_tax_amount?: number;
};

// Shipping option export type (used by other components)
export type ShippingOption = {
  shipping__id: string;
  shipping_name?: string;
  shipping_amount: number;
  shipping_tax_percentage: number;
  shipping_tax_amount: number;
  shipping_taxable: boolean;
  shipping_delivarydate?: string;
  shipping_delivery_time?: string;
};

// Special fee export type (used by other components)
export type SpecialFee = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number;
  specialfee_tax_amount: number;
  specialfee_taxable: boolean;
};


// ============================================================================
// HELPER FUNCTIONS & CONSTANTS
// ============================================================================
// Product totals calculation type
type ProductTotals = { subTotal: number; discount: number; productTax: number };

// Number conversion helper - safely converts any value to number (defaults to 0)
const n = (v: any) => Number(v || 0);

// Normalize shipping rows to consistent format [id, amount, tax_amount, taxable]
const normShip = (rows?: any[] | null) =>
  (rows ?? []).map((r: any) => [r.shipping_id ?? "", n(r.shipping_amount), n(r.shipping_tax_amount), r.shipping_taxable !== false]);

// Normalize special fee rows to consistent format [id, amount, tax_amount, taxable]
const normFee = (rows?: any[] | null) =>
  (rows ?? []).map((r: any) => [r.specialfee_id ?? "", n(r.specialfee_amount), n(r.specialfee_tax_amount), r.specialfee_taxable !== false]);

// Google Maps API configuration
const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// Table column helper setup
type CartItemRow = CartItem;
const ch = createColumnHelper<CartItemRow>();

// Stored fee type for special fees
type StoredFee = { id: string; amount: number; taxable: boolean; title: string; required?: boolean };

// ============================================================================
// MAIN COMPONENT: AgentOrderCheckout
// ============================================================================
// This component handles the order checkout process for agents.
// It manages product selection, addresses, shipping, taxes, discounts, and payment.
const AgentOrderCheckout = () => {
  // ========================================================================
  // ROUTING & PARAMETERS
  // ========================================================================
  const { signageId, productId } = useParams<{
    signageId: string;
    productId: string;
  }>();

  // ========================================================================
  // AUTHENTICATION & USER INFO
  // ========================================================================
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const license = getAuthCookie("licenseType");
  const supplierId = localStorage.getItem("supplierId") || "";
  const cartId = localStorage.getItem("cart_id");
  
  // State for parent brokerage firm (for BROKERAGE_AGENT login)
  const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
  const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);

  // ========================================================================
  // UI STATE - Loading & Errors
  // ========================================================================
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pills-bill-info-tab");
  const [tabCount, setTabCount] = useState(1);
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const pendingInstructionFilesRef = useRef<{ id: string; file: File }[]>([]);
  const handlePendingInstructionFilesChange = useCallback((items: { id: string; file: File }[]) => {
    pendingInstructionFilesRef.current = items;
  }, []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [getstatusTaxType, setGetStatusTaxType] = React.useState("");

  // ========================================================================
  // PRODUCT & CART STATE
  // ========================================================================
  const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartHeader, setCartHeader] = useState<any>(null); // Holds discount/subtotal/total/charges from cart GET

  // ========================================================================
  // BUYER & ORGANIZATION SELECTION STATE
  // ========================================================================
  const [selectedAgent, setSelectedAgent] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const [selectedOffice, setSelectedOffice] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  // ========================================================================
  // ADDRESS STATE
  // ========================================================================
  const [address, setAddress] = useState("");
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
  const [userInfo, setUserInfo] = useState<License>();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // ========================================================================
  // ORDER STATE
  // ========================================================================
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState<Order | null>(null);

  // ========================================================================
  // ORDER TOTALS & FINANCIAL STATE
  // ========================================================================
  const [subTotal, setSubTotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedTaxRate, setSelectedTaxRate] = useState(0);
  const [enableNet30, setEnableNet30] = useState(false);

  // ========================================================================
  // SHIPPING STATE
  // ========================================================================
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [selectedShippingOption, setSelectedShippingOption] = useState<string>("");
  const [isLoadingUps, setIsLoadingUps] = useState(false);

  // ========================================================================
  // PAYMENT STATE
  // ========================================================================
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [clientSecret, setClientSecret] = useState<string | "">();
  const [groups, setGroups] = useState<Group[]>([]);

  // ========================================================================
  // HELPER FUNCTIONS - Product Management
  // ========================================================================
  const addProductRow = () => setPickerOpen(true);
  const closePicker = () => setPickerOpen(false);
  const removeProductRow = (index: number) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const [partialAmount, setPartialAmount] = useState<number>(0);
  const [showAddressForm, setShowAddressForm] = useState(false);
  // const [canProceedToPayment, setCanProceedToPayment] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [checkoutPaymentOpen, setCheckoutPaymentOpen] = useState(false);
  const [checkoutPaymentOrder, setCheckoutPaymentOrder] = useState<Record<string, unknown> | null>(null);
  const dispatch = useAppDispatch();
  const { fetchCart, clearCartItems } = useCart();
  const [canProceedToPayment, setCanProceedToPayment] = useState(false);
  const [shippingOptionError, setShippingOptionError] = useState(false);
  const [upsOptions, setUpsOptions] = useState<UpsUIOption[]>([]);
  const [billingAddress, setBillingAddress] = useState<Addresses>();
  const [shippingAddress, setShippingAddress] = useState<Addresses>();

  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Addresses | null>(null);

  const [selectedShippingAddress, setSelectedShippingAddress] =
    useState<Addresses | null>(null);

  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

  // âœ… Centralized busy state tracking
  const [busyStates, setBusyStates] = useState({
    shipping: false,
    specialFee: false,
    productTax: false,
    mainForm: false,
  });

  // Compute if ANY operation is busy
  const isAnythingBusy = React.useMemo(
    () => formSubmitting || Object.values(busyStates).some(b => b),
    [formSubmitting, busyStates]
  );

  // Callbacks for child components to report their busy state
  const handleShippingBusyChange = React.useCallback((busy: boolean) => {
    setBusyStates(prev => ({ ...prev, shipping: busy }));
  }, []);

  const handleSpecialFeeBusyChange = React.useCallback((busy: boolean) => {
    setBusyStates(prev => ({ ...prev, specialFee: busy }));
  }, []);

  // shippingAddress,brackdowns,specicakfee useStates
  // Live/show switching like SignageEdit
  const [liveShipping, setLiveShipping] = useState(0);
  const [liveSpecialFee, setLiveSpecialFee] = useState(0);
  const [useLiveTotals, setUseLiveTotals] = useState(true);
  const [productTaxMap, setProductTaxMap] = useState<Record<string, number>>({});
  const [taxBreakdown, setTaxBreakdown] = useState({ product: 0, shipping: 0, specialFee: 0 });
  const [shipPayload, setShipPayload] = React.useState<ShipPayload | null>(null);
  const [feePayload, setFeePayload] = React.useState<FeePayload | null>(null);
  // === Coupon state (per-item split from resolve) ===
  const [perItemCouponMap, setPerItemCouponMap] = React.useState<Record<string, number>>({});
  const [couponTouched, setCouponTouched] = React.useState(false);
  const [productTotals, setProductTotals] = React.useState<ProductTotals>({
    subTotal: 0,
    discount: 0,
    productTax: 0,
  });
  const shipRef = React.useRef<ShippingOptionsV2Handle>(null);
  const feeRef = React.useRef<SpecialFeePickerV2Handle>(null);

  const USE_SALES_ORDER_RESOLVE = true;
  const lastResolvedSalesRef = React.useRef<ResolvedSalesOrder | null>(null);
  const isRecomputingRef = React.useRef(false);
  const [isRecomputingSales, setIsRecomputingSales] = React.useState(false);
  const [couponInput, setCouponInput] = React.useState("");
  const [couponResolveError, setCouponResolveError] = React.useState("");
  const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
  const [resolvedTotalsTick, setResolvedTotalsTick] = React.useState(0);
  const userClearedCouponRef = React.useRef(false);
  const recomputeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const shipPayloadRef = React.useRef<ShipPayload | null>(null);
  const feePayloadRef = React.useRef<FeePayload | null>(null);
  const specialFeeCatalogRef = React.useRef<unknown[]>([]);
  const recomputeSalesRef = React.useRef<
    ((override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload | null;
      shipPayload?: ShipPayload | null;
    }) => Promise<boolean>) | null
  >(null);
  const formikValuesRef = React.useRef<Record<string, unknown>>({});
  const checkoutUiTotalsRef = React.useRef({
    grandTotal: 0,
    discount: 0,
    subtotal: 0,
    tax: 0,
  });

  const handleShipPayloadChange = React.useCallback((p: ShipPayload) => {
    shipPayloadRef.current = p;
    setShipPayload(prev => {
      const prevKey = JSON.stringify({
        rows: (prev?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: prev?.selected_ids, ups: !!prev?.isUps, man: !!prev?.isManual
      });
      const nextKey = JSON.stringify({
        rows: (p?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: p?.selected_ids, ups: !!p?.isUps, man: !!p?.isManual
      });
      return (prevKey === nextKey) ? prev : p;
    });
    if (USE_SALES_ORDER_RESOLVE) {
      if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
      recomputeTimerRef.current = setTimeout(() => {
        void recomputeSalesRef.current?.({ shipPayload: p });
      }, 300);
    }
  }, []);

  const taxcloudCartIdRef = React.useRef<string>("");
  const upsertLinesKeyRef = useRef<string>("");
  const runIdRef = React.useRef(0);
  const [activeTaxType, setActiveTaxType] = React.useState<"manual" | "taxcloud" | "geera">("manual");
  const [taxError, setTaxError] = React.useState<string>("");

  const asNum = (v: any) => Number(v || 0);
  const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;
  const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;
  const asArray = <T,>(val: T | T[] | null | undefined): T[] =>
    Array.isArray(val) ? val : (val ? [val] : []);
  const money = (v: any) =>
    Number.isFinite(Number(v)) ? `$${Number(v).toFixed(2)}` : "TBD";

  const shippingOptions = shippingCosts?.map((cost) => ({
    value: cost._id,
    label: cost.name,
    description: cost.description,
    cost: cost.is_free ? 0 : (cost.cost ?? 0),
    is_free: cost.is_free,
    id: cost._id,
    is_taxable: cost.is_taxable,
    taxable: cost.taxable
  }));
  const location = useLocation();
  const navigationOrderId = String(location.state?.orderId ?? "").trim() || null;
  const [linkedOrderIdFromCart, setLinkedOrderIdFromCart] = useState<string | null>(null);
  const [linkedOrderNumberFromCart, setLinkedOrderNumberFromCart] = useState<string | null>(null);
  const resumeOrderId = linkedOrderIdFromCart || navigationOrderId || "";
  const [editOrder, setEditOrder] = React.useState<any>(null);
  const isEdit = Boolean(resumeOrderId);

  useEffect(() => {
    async function fetchOrderMetadata() {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/${resumeOrderId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to fetch order data");
        const data = await res.json();
        setEditOrder(data);
        setOrder(data);
        setOrderId(resumeOrderId);

        if (data?.billing_address) {
          setSelectedBillingAddress(data.billing_address);
        }
        const sameAsBilling =
          data?.billing_address?._id &&
          data?.shipping_address?._id &&
          data.billing_address._id === data.shipping_address._id;
        if (sameAsBilling && data?.billing_address) {
          setSelectedShippingAddress(data.billing_address);
        } else if (data?.shipping_address) {
          setSelectedShippingAddress(data.shipping_address);
        }

        // Product rows come from cart when cart_id exists.
        if (!cartId) {
          setCartItems(Array.isArray(data?.items) ? data.items : []);
        }

        setInitialValues(mapOrderToForm(data));
        if (data?.discount_code) {
          const code = String(data.discount_code).trim();
          setCouponInput(code);
          setLiveDiscountDisplay({
            code,
            amount: Number(data.discount_amount || 0),
          });
        }
        hydratedRef.current = true;
        setHydrated(true);
      } catch (e) {
        await handleApiError(e, {
          module: "sales_order",
          action: "agent_order_checkout_fetch_order_data",
          userId: userId || undefined,
          url: `${process.env.REACT_APP_API_URL}/orders/${resumeOrderId}`,
          method: "GET",
          additionalInfo: { orderId: resumeOrderId }
        }, token);
        safeConsole.error("Error fetching order data:", e);
      } finally {
        setLoading(false);
      }
    }

    if (isEdit && token) fetchOrderMetadata();
    else if (!navigationOrderId && !linkedOrderIdFromCart) setInitialValues(emptyInitialValues);
  }, [isEdit, resumeOrderId, token, cartId, navigationOrderId, linkedOrderIdFromCart]);

    const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const emptyInitialValues: InitialValues = {
    userFirstName: userInfo?.firstName || "",
    userLastName: userInfo?.lastName || "",
    userEmail: userInfo?.email || "",
    userPhone: userInfo?.phone1 || "",
    // officeAgentId: "",
    // teamMemberId: "",
    // officeId: "",
    // office: "",
    // agent: "",
    agent_id: "",
    label_of_address: "",
    firstName: "",
    lastName: "",
    company: "",
    addressLine1: "",
    addressLine2: "",
    country: "",
    county: "",
    city: "",
    state: "",
    zipcode: "",
      phone: DEFAULT_TEN_ZEROS || "",
    billingAddressId: "",
    shippingAddressId: "",
    shippingMethod: "free",
    isSameAsBilling: false,
    notes: "",
    po_number: "",
  };
  const [initialValues, setInitialValues] = React.useState<InitialValues>(emptyInitialValues);
  // ---- 2) Helper: map API -> form fields for EDIT mode
  function mapOrderToForm(data: any): InitialValues {
    const billing = data?.billing_address || {};
    const shipping = data?.shipping_address || {};
    if (data?.signage_id) {
      localStorage.setItem("supplierId", data.signage_id);
    }

    const sameAsBilling = billing?._id && shipping?._id ? billing._id === shipping._id : true;
    const contact = formikValuesRef.current as Partial<InitialValues>;

    return {
      userFirstName:
        data?.customer_first_name || contact.userFirstName || userInfo?.firstName || "",
      userLastName:
        data?.customer_last_name || contact.userLastName || userInfo?.lastName || "",
      userEmail: data?.customer_email || contact.userEmail || userInfo?.email || "",
      userPhone: data?.customer_phone || contact.userPhone || userInfo?.phone1 || "",

      // // set these if you have them; otherwise keep as blank
      // agent: data?.customer_first_name ? `${data.customer_first_name} ${data.customer_last_name || ""}`.trim() : "",
      agent_id: data?.agent_id || "",

      // If you want to show label on formâ€™s address block too
      label_of_address: billing?.label || "",

      // If you want â€œmain addressâ€ fields to reflect billing by default
      firstName: data?.customer_first_name || contact.userFirstName || userInfo?.firstName || "",
      lastName: data?.customer_last_name || contact.userLastName || userInfo?.lastName || "",
      company: data?.brokerage_firm_name || "",
      addressLine1: billing?.address_details?.addressLine1 || "",
      addressLine2: billing?.address_details?.addressLine2 || "",
      country: billing?.address_details?.country || "",
      city: billing?.address_details?.city || "",
      county: "", // not in API
      state: billing?.address_details?.state || "",
      zipcode: billing?.address_details?.zip || "",
      phone: billing?.address_details?.phone || "",

      billingAddressId: billing?._id || "",
      shippingAddressId: shipping?._id || "",

      // derive a usable shippingMethod string (fallback to "free")
      shippingMethod:
        data?.shipping_method?.service_name ||
        (data?.manual_shipping_method?.is_free ? "free" : (data?.manual_shipping_method?.name || "free")),

      isSameAsBilling: sameAsBilling,

      // put notes here if you store them (not in your sample)
      notes: data?.notes || "",
      po_number: String(data?.po_number ?? data?.poNumber ?? "").trim(),
    };
  }

  // If EDIT mode data arrives, map it; otherwise fall back to userInfo
  React.useEffect(() => {
    // If you have edit data:
    if (editOrder) {
      setInitialValues(mapOrderToForm(editOrder));
      return;
    }

    // Otherwise hydrate from userInfo
    setInitialValues(prev => ({
      ...prev,
      userFirstName: userInfo?.firstName || "",
      userLastName: userInfo?.lastName || "",
      userEmail: userInfo?.email || "",
      userPhone: userInfo?.phone1 || "",
      // (leave the rest as-is)
    }));
  }, [userInfo, editOrder]);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${supplierId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch integrations.");
        }
        const data = await response.json();
        setIntegrations(data);
      } catch (err) {
        // showError("Failed to load integrations.");
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [token]);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
    // safeConsole.log('Stripe info ', stripeInfo);
    if (stripeInfo && stripeInfo.publishable_key) {
      const promise = loadStripe(stripeInfo.publishable_key);
      setStripePromise(promise);
    }
  }, [integrations]);

  // payload calculation helper

  const getSelectedShippingMeta = () => {
    const fromStatic = shippingOptions?.find(opt => opt.value === selectedShippingOption);
    const fromUps = (upsOptions || []).find(o => `ups:${o.code}` === selectedShippingOption);
    if (fromStatic) {
      return {
        amount: Number(fromStatic.cost || 0),
        isTaxable: fromStatic.is_taxable ?? true,
        method: {
          carrier_name: "Manual",
          service_name: fromStatic.label || "Manual Shipping",
          tracking_number: "",
          cost: Number(fromStatic.cost || 0),
          service_code: fromStatic.id,
        }
      };
    }
    if (fromUps) {
      return {
        amount: Number(fromUps.cost || 0),
        isTaxable: true,
        method: {
          carrier_name: "UPS",
          service_name: fromUps.service || "UPS",
          tracking_number: "",
          cost: Number(fromUps.cost || 0),
          service_code: fromUps.code,
        }
      };
    }
    return {
      amount: 0,
      isTaxable: false,
      method: {
        carrier_name: "",
        service_name: "",
        tracking_number: "",
        cost: 0,
        service_code: "",
      }
    };
  };

  const formik = useFormik<InitialValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      userFirstName: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      userEmail: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      userPhone: Yup.string()
        .required("Phone number is required"),
      billingAddressId: Yup.string()
        .required("Billing address is required"),
      shippingAddressId: Yup.string().when("isSameAsBilling", {
        is: false,
        then: (schema) => schema.required("Shipping address is required"),
        otherwise: (schema) => schema,
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setActiveTab("pills-finish-tab");
      setSubmitting(false);
    },
  });

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        ...address,
        value: address._id,
        label: `${address?.label}: ${address?.address_details?.addressLine1}, ${address?.address_details?.city}, ${address?.address_details?.state}, ${address?.address_details?.zip ? address?.address_details?.zip : ''}`,
      }))
      : [];


  const handleAddFromPicker = (payload: any) => {
    const validDims = (d: any) =>
      d &&
      Number.isFinite(d.length) && d.length > 0 &&
      Number.isFinite(d.width) && d.width > 0 &&
      Number.isFinite(d.height) && d.height > 0 &&
      Number.isFinite(d.weight) && d.weight > 0;

    const hasPreparedPayload =
      payload &&
      payload.product_id &&
      payload.variant_id &&
      payload.customizations &&
      Array.isArray(payload.customizations.fields);

    const variantFromPayload =
      payload?.product?.variants?.find((vv: any) => vv.variant_id === payload?.variant_id) ||
      payload?.product?.variants?.[0];

    const rawDims =
      variantFromPayload?.package_dimensions ??
      payload?.product?.package_dimensions ??
      payload?.package_dimensions;

    // Helper to derive storage location if not provided
    const deriveStorageFromProduct = (productobj: any) => {
      const p = productobj?.product;
      const v =
        (Array.isArray(p?.variants) && p.variants.find((vv: any) => vv.variant_id === productobj?.variant_id)) ||
        (Array.isArray(p?.variants) && p.variants[0]) ||
        undefined;

      const invList: any[] =
        (Array.isArray(v?.inventory) && v!.inventory) ||
        (Array.isArray(p?.inventory) && p!.inventory) ||
        [];

      const invEntry: any = invList.find((e) => (e?.available ?? 0) > 0) || invList[0] || null;

      return invEntry
        ? {
          bin_id: invEntry.bin_id || "",
          bin: invEntry.bin_name || "",
          rack_id: invEntry.rack_id || "",
          rack: invEntry.rack_name || "",
          warehouse_id: invEntry.warehouse_id || "",
          warehouse: invEntry.warehouse_name || "",
          zone_id: invEntry.zone_id || "",
          zone: invEntry.zone_name || "",
        }
        : {
          bin_id: "",
          bin: "",
          rack_id: "",
          rack: "",
          warehouse_id: "",
          warehouse: "",
          zone_id: "",
          zone: "",
        };
    };

    // Build the source object weâ€™ll map to CartItem
    const src = hasPreparedPayload
      ? payload
      : {
        // fallback: derive from product object (no customization values in this path)
        product_id: payload?.product_id,
        variant_id: payload?.variant_id || payload?.product?.variants?.[0]?.variant_id || "",
        name: payload?.name || payload?.product?.variants?.[0]?.variant_name || payload?.product?.name || "",
        sku: payload?.sku || payload?.product?.variants?.[0]?.sku || payload?.product?.sku || "",
        unit_purchase_price:
          payload?.unit_purchase_price ??
          payload?.product?.variants?.[0]?.purchase_price ??
          payload?.product?.purchase_price ??
          0,
        unit_retail_price:
          payload?.unit_retail_price ??
          payload?.product?.variants?.[0]?.retail_price ??
          payload?.product?.retail_price ??
          0,
        quantity: 1,
        tax_details: {
          tax_rate: payload?.product?.tax_rule?.taxRate ?? 0,
          tax_amount: 0,
          applied_state: payload?.product?.tax_rule?.state ?? "",
        },
        tax_rule: payload?.product?.tax_rule || {},
        tax_group: payload?.product?.tax_group || {},
        image_url:
          (Array.isArray(payload?.product?.images) && payload.product.images[0]?.url) ||
          payload?.product?.image?.url ||
          "",
        storage_location:
          payload?.storage_location && payload.storage_location.warehouse_id
            ? payload.storage_location
            : deriveStorageFromProduct(payload),
        customizations: {
          template_name:
            payload?.customizations?.template_name ||
            payload?.product?.variants?.[0]?.variant_name ||
            payload?.product?.name ||
            "",
          fields:
            (payload?.customizations?.fields || []).map((f: any) => ({
              field_name: f.field_name,
              field_type: f.field_type,
              required: !!f.required,
              value: f.value ?? "", // nothing to wipe here
            })) || [],
        },
        amount: 0,
        // ðŸ‘‡ include dims ONLY if all fields are > 0; otherwise omit
        ...(validDims(rawDims)
          ? {
            package_dimensions: {
              length: rawDims.length,
              width: rawDims.width,
              height: rawDims.height,
              weight: rawDims.weight,
              ...(rawDims.weight_type ? { weight_type: rawDims.weight_type } : {}),
            },
          }
          : {}),
      };

    // prevent duplicates
    const duplicate = cartItems.some(
      (ci) => ci.product_id === src.product_id && ci.variant_id === src.variant_id
    );
    if (duplicate) {
      // toast/alert optional
      // toast.warning("This product is already added");
      return;
    }

    // Map -> CartItem (preserve customization values!)
    const item: CartItem = {
      product_id: src.product_id,
      variant_id: src.variant_id,
      product_name: src.name,
      storage_location: {
        bin_id: src.storage_location?.bin_id || "",
        bin: src.storage_location?.bin || "",
        rack_id: src.storage_location?.rack_id || "",
        rack: src.storage_location?.rack || "",
        warehouse_id: src.storage_location?.warehouse_id || "",
        warehouse: src.storage_location?.warehouse || "",
        zone_id: src.storage_location?.zone_id || "",
        zone: src.storage_location?.zone || "",
      },
      warehouse_id: src.storage_location?.warehouse_id || "",
      name: src.name || "",
      sku: src.sku || "",
      unit_purchase_price: Number(src.unit_purchase_price || 0),
      unit_retail_price: Number(src.unit_retail_price || 0),
      quantity: Number(src.quantity || 1),
      tax_details: {
        tax_rate: Number(src?.tax_details?.tax_rate || 0),
        tax_amount: Number(src?.tax_details?.tax_amount || 0),
        applied_state: src?.tax_details?.applied_state || "",
      },
      item_tax_info: [],
      image_url: src.image_url || "",
      images: src.images,
      amount:
        typeof src.amount === "number"
          ? src.amount
          : Number(src.quantity || 1) * Number(src.unit_retail_price || 0),
      tax_rule: src.tax_rule || ({} as TaxRule),
      tax_group: src.tax_group || ({} as TaxGroup),
      ...(src.package_dimensions ? { package_dimensions: src.package_dimensions } : {}), // only if valid
      customizations: src.customizations
        ? {
          template_name: src.customizations.template_name || "",
          fields: (src.customizations.fields || []).map((f: any) => ({
            field_name: f.field_name,
            field_type: f.field_type,
            required: !!f.required,
            value: f.value ?? "", // <-- keep value
          })),
        }
        : { template_name: "", fields: [] },
    };

    const next = [...cartItems, item];
    setCartItems(next);
    setPickerOpen(false);
  };

  const checkProceedEligibility = (billingId?: string, shippingId?: string) => {
    const billing = addressObjects?.find((address) => address._id === billingId);
    const shipping = addressObjects?.find((address) => address._id === shippingId);

    if (formik.values.isSameAsBilling) {
      setCanProceedToPayment(!!billing);
    } else {
      setCanProceedToPayment(!!billing && !!shipping);
    }
  };
  useEffect(() => {
    checkProceedEligibility(formik.values.billingAddressId, formik.values.shippingAddressId);
  }, [
    formik.values.billingAddressId,
    formik.values.shippingAddressId,
    formik.values.isSameAsBilling
  ]);

  // Fetch parent hierarchy for BROKERAGE_AGENT login
  const fetchParentHierarchy = async () => {
    if (license !== "BROKERAGE_AGENT" || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        safeConsole.log("Parent hierarchy data:", data);
        
        // Find the parent with license_type "BROKERAGE_FIRM"
        const brokerageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "BROKERAGE_FIRM"
        );

        if (brokerageFirm) {
          setParentBrokerageId(brokerageFirm.id || brokerageFirm._id);
          setParentBrokerageName(brokerageFirm.name);
          safeConsole.log("âœ… Parent Brokerage Firm found:", {
            id: brokerageFirm.id || brokerageFirm._id,
            name: brokerageFirm.name
          });
        }
      }
    } catch (error) {
      safeConsole.error("Error fetching parent hierarchy:", error);
    }
  };

  // Fetch parent hierarchy on mount
  useEffect(() => {
    if (license === "BROKERAGE_AGENT" && token) {
      fetchParentHierarchy();
    }
  }, [license, token]);

  // âœ… Compute effective createdById based on license type
  const effectiveCreatedById = React.useMemo(() => {
        const id = license === "BROKERAGE_AGENT" && parentBrokerageId 
      ? userId || ""
      : userId || "";
    // const id = license === "BROKERAGE_AGENT" && parentBrokerageId 
    //   ? parentBrokerageId 
    //   : userId || "";
    
    // safeConsole.log("ðŸ†” Effective Created By ID:", {
    //   license,
    //   userId,
    //   parentBrokerageId,
    //   effectiveId: id
    // });
    
    return id;
  }, [license, parentBrokerageId, userId]);
  
  const fetchAddresses = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch addresses: ${response.statusText}`);
      }
      const data = await response.json();
      setAddressObjects(data);
    } catch (err) {
      await handleApiError(err, {
        module: "sales_order",
        action: "agent_order_checkout_fetch_addresses",
        userId: userId || undefined,
        url: `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
        method: "GET",
        additionalInfo: { userId }
      }, token);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAddresses();
    }
  }, [userId, token]);

  // Load cart line items always; detect linked draft order for resume mode.
  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/carts/${cartId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch product data: ${response.statusText}`);
        }

        const data = await response.json();
        const linkedId = getResumeOrderIdFromCart(data);
        const linkedNumber = getResumeOrderNumberFromCart(data);

        setLinkedOrderIdFromCart(linkedId);
        setLinkedOrderNumberFromCart(linkedNumber);
        if (linkedId) setOrderId(linkedId);

        const lineItems = getLineItemsFromCart(data);
        setCartItems(lineItems);

        if (!linkedId) {
          setCartHeader(buildCartHeaderFromCart(data));
          const hydratedCoupon = buildPerItemCouponMapFromCartItems(lineItems);
          setPerItemCouponMap((prev) => ({ ...hydratedCoupon, ...prev }));
          const header = buildCartHeaderFromCart(data);
          if (header.discount_code) {
            const code = String(header.discount_code).trim();
            setCouponInput(code);
            setLiveDiscountDisplay({
              code,
              amount: Number(header.discount_amount || 0),
            });
          }
        }

        hydratedRef.current = true;
        setHydrated(true);
      } catch (err) {
        safeConsole.error("Error fetching product data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (cartId) fetchCartItems();
  }, [cartId, token]);

  useEffect(() => {
    const selectedOption = shippingOptions?.find(opt => opt.value === selectedShippingOption);
    const shippingCost = selectedOption ? selectedOption.cost : 0;
    const newTotal = subTotal + tax + shippingCost;
    setTotalAmount(newTotal);
  }, [selectedShippingOption, shippingOptions, subTotal, tax]);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        const data = await response.json();
        if (data && data.firmDetails && data.firmDetails.address) {
          setUserInfo(data);
        } else {
          safeConsole.error("Incomplete data", data);
        }
      } catch (error) {
        safeConsole.error("Error fetching user data:", error);
      }
    };

    if (userId) {
      fetchUserInfo();
    }
  }, [userId, token]);

  // useEffect(() => {
  //   const checkCreditFacility = async () => {
  //     const net30GroupId = "67bc75781a32a989c4e6477c";
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/customer-groups/${net30GroupId}/users/${userId}/exists`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch data");
  //       }
  //       const data = await response.json();
  //       if (data) {
  //         if (data.exists) {
  //           setEnableNet30(true);
  //         }
  //       } else {
  //         safeConsole.error("Incomplete data", data);
  //       }
  //     } catch (error) {
  //       safeConsole.error("Error fetching user data:", error);
  //     }
  //   };

  //   if (userId) {
  //     checkCreditFacility();
  //   }
  // }, [userId, token]);

  // Fetch addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch addresses data");
        }
        const data = await response.json();
        if (data) {
          setAddresses(data);
        } else {
          safeConsole.error("Incomplete data", data);
        }
      } catch (error) {
        safeConsole.error("Error fetching addresses data:", error);
      }
    };

    if (userId) {
      fetchAddresses();
    }
  }, [userId, token]);

  function buildItemTaxInfoAtCreate(
    row: { productId: string; variantId: string; sku: string; unitPrice: number; quantity: number },
    productTaxMap: Record<string, number>,
    shippingAddress: any
  ) {
    const k = keyForRow(row);
    const lineTax = asNum(productTaxMap[k] || 0);           // total tax for this line
    const qty = Math.max(1, asNum(row.quantity || 1));
    const unitTax = qty > 0 ? lineTax / qty : 0;            // per-unit tax
    const base = asNum(row.unitPrice || 0) * qty;        // pre-tax line base
    const ratePct = base > 0 ? (lineTax / base) * 100 : 0;  // % for display
    const appliedState = String(
      shippingAddress?.address_details?.stateCode ||
      shippingAddress?.address_details?.state || ""
    );


    return {
      tax_percentage: round2(ratePct),
      unit_product_tax_amount: round2(unitTax),
      total_product_tax_amount: round2(lineTax),
      tax_details: {
        tax_rate: round2(ratePct),
        tax_amount: round2(lineTax),
        applied_state: appliedState,
      },
      taxable: lineTax > 0 || ratePct > 0,
    };
  }

  const getUnitRetailPriceForPayload = React.useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const item = (cartItems || []).find(
        (ci) => String(ci.product_id) === productId && String(ci.variant_id) === variantId
      );
      return Number(item?.unit_retail_price ?? originalUnitPrice ?? 0);
    },
    [cartItems]
  );

  const getChangedUnitPriceForPayload = React.useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const item = (cartItems || []).find(
        (ci) => String(ci.product_id) === productId && String(ci.variant_id) === variantId
      );
      return Number(
        item?.changed_unit_price ?? item?.unit_retail_price ?? originalUnitPrice ?? 0
      );
    },
    [cartItems]
  );

  const applyResolvedSalesToUi = React.useCallback(
    (resolved: ResolvedSalesOrder, requestedDiscountCode?: string) => {
      const sync = applySalesResolveResponse(resolved, {
        requestedDiscountCode,
        userClearedCoupon: userClearedCouponRef.current,
        existingFeeRows: feePayloadRef.current?.specialfee,
        existingShipRows: shipPayloadRef.current?.shipping_option,
        specialFeeCatalog: specialFeeCatalogRef.current,
        shippingCostCatalog: shippingCosts,
      });
      setProductTaxMap(sync.productTaxMap);
      setTaxBreakdown(sync.taxBreakdown);
      setProductTotals(sync.productTotals);
      if (sync.feePayloadPatch?.specialfee?.length) {
        const resolvedFees = sync.feePayloadPatch.specialfee;
        const nextFeePayload: FeePayload = {
          specialfee: resolvedFees as FeeRow[],
          selected_fee_ids: (resolvedFees as Array<{ specialfee_id?: string }>)
            .map((f) => String(f.specialfee_id ?? ""))
            .filter(Boolean),
          total_amount: sync.feePayloadPatch.total_amount,
          total_tax_amount: sync.feePayloadPatch.total_tax_amount,
        };
        feePayloadRef.current = nextFeePayload;
        setFeePayload(nextFeePayload);
      }
      if (sync.shipPayloadPatch?.shipping_option?.length) {
        const nextShip: ShipPayload = {
          ...(shipPayloadRef.current ?? shipPayload ?? {}),
          shipping_option: sync.shipPayloadPatch.shipping_option as ShipPayload["shipping_option"],
        };
        shipPayloadRef.current = nextShip;
        setShipPayload(nextShip);
      }
      setCartItems((prev) => mergeCheckoutCartFromResolved(prev, resolved));
      setPerItemCouponMap(perItemCouponMapFromResolved(resolved));
      setLiveDiscountDisplay(sync.discountDisplay);
      setResolvedTotalsTick((t) => t + 1);
    },
    [shipPayload, shippingCosts]
  );

  const getCheckoutChargeSource = React.useCallback(
    () =>
      editOrder ||
      order ||
      (cartHeader ? { order_charges: cartHeader.order_charges } : null),
    [editOrder, order, cartHeader]
  );

  const readLiveShipFeePayloads = React.useCallback(() => {
    const shipFromChild = shipRef.current?.getPayload?.();
    const feeFromChild = feeRef.current?.getPayload?.();
    const activeShipPayload =
      (Array.isArray(shipFromChild?.shipping_option) && shipFromChild.shipping_option.length
        ? shipFromChild
        : null) ??
      shipPayloadRef.current ??
      shipPayload;
    const activeFeePayload =
      (Array.isArray(feeFromChild?.specialfee) && feeFromChild.specialfee.length
        ? feeFromChild
        : null) ??
      feePayloadRef.current ??
      feePayload;
    return { activeShipPayload, activeFeePayload };
  }, [shipPayload, feePayload]);

  const getLiveCheckoutChargeRows = React.useCallback(() => {
    const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
    return getCheckoutOrderChargeFallbacks({
      shipPayload: toCheckoutChargeShipPayload(activeShipPayload),
      feePayload: activeFeePayload,
      savedOrder: getCheckoutChargeSource(),
    });
  }, [readLiveShipFeePayloads, getCheckoutChargeSource]);

  const recomputeSalesOrder = React.useCallback(
    async (override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload | null;
      shipPayload?: ShipPayload | null;
    }) => {
      if (!USE_SALES_ORDER_RESOLVE || !token) return false;
      if (isRecomputingRef.current && !override?.force) return false;
      if (!supplierId || !(cartItems?.length)) return false;

      const buyer = pickAgentCheckoutResolveBuyer({
        userId: userId || "",
        userName: userName || "",
        userInfo,
        selectedBillingAddress,
        selectedShippingAddress,
        isSameAsBilling: !!formikValuesRef.current.isSameAsBilling,
      });
      if (!buyer.customerId) return false;

      const discountCode = getSalesResolveDiscountCode({
        override: override?.discountCode,
        userClearedCoupon: userClearedCouponRef.current,
        couponInput,
        liveDiscountCode: liveDiscountDisplay.code,
        savedDiscountCode: isEdit
          ? editOrder?.discount_code
          : cartHeader?.discount_code,
      });

      isRecomputingRef.current = true;
      setIsRecomputingSales(true);
      setCouponResolveError("");

      try {
        const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
        const overrideShip = override?.shipPayload ?? activeShipPayload;
        const overrideFee = override?.feePayload ?? activeFeePayload;
        const chargeRows = getCheckoutOrderChargeFallbacks({
          shipPayload: toCheckoutChargeShipPayload(overrideShip),
          feePayload: overrideFee,
          savedOrder: getCheckoutChargeSource(),
        });

        const payload = buildSalesResolvePayload({
          signageId: supplierId,
          buyerType: buyer.buyerType,
          customerId: buyer.customerId,
          agentId: buyer.agentId,
          agentName: buyer.agentName,
          customerFirstName: buyer.customerFirstName,
          customerLastName: buyer.customerLastName,
          customerEmail: buyer.customerEmail,
          customerPhone: buyer.customerPhone,
          billingAddress: buyer.billingAddress,
          shippingAddress: buyer.shippingAddress,
          bothAddressSame: buyer.bothAddressSame,
          inventorySource: buyer.inventorySource,
          discountCode,
          cartId: cartId ?? null,
          orderId: isEdit ? resumeOrderId : null,
          orderNumber: isEdit ? String(editOrder?.order_number || "").trim() || null : null,
          orderTaxStatus: editOrder?.order_tax_summary?.tax_status ?? "",
          selectedProducts: mapCheckoutCartToResolveCatalogRows(cartItems),
          shipPayload: overrideShip,
          feePayload: overrideFee,
          fallbackShippingRows: chargeRows.shippingRows,
          fallbackFeeRows: chargeRows.feeRows,
          shippingCosts,
          specialFeeCatalog: specialFeeCatalogRef.current,
          manualUnitPriceMap: {},
          getUnitRetailPriceForPayload,
          getChangedUnitPriceForPayload,
        });

        const resolved = await postSalesOrderResolve(token, {
          order_type: "order",
          action: "draft",
          payload,
        });

        lastResolvedSalesRef.current = resolved;
        applyResolvedSalesToUi(resolved, discountCode);
        return true;
      } catch (err: unknown) {
        const msg = String((err as Error)?.message ?? "Failed to recalculate order");
        setCouponResolveError(msg);
        showError(msg);
        return false;
      } finally {
        isRecomputingRef.current = false;
        setIsRecomputingSales(false);
      }
    },
    [
      token,
      supplierId,
      cartItems,
      cartId,
      couponInput,
      liveDiscountDisplay.code,
      cartHeader?.discount_code,
      isEdit,
      resumeOrderId,
      editOrder,
      shipPayload,
      feePayload,
      shippingCosts,
      userId,
      userName,
      userInfo,
      selectedBillingAddress,
      selectedShippingAddress,
      getUnitRetailPriceForPayload,
      getChangedUnitPriceForPayload,
      applyResolvedSalesToUi,
      readLiveShipFeePayloads,
      getCheckoutChargeSource,
    ]
  );

  recomputeSalesRef.current = recomputeSalesOrder;

  React.useEffect(() => {
    formikValuesRef.current = formik.values as Record<string, unknown>;
  }, [formik.values]);

  const shippingAddressSig = React.useMemo(() => {
    const addr =
      (formik.values.isSameAsBilling ? selectedBillingAddress : selectedShippingAddress) ??
      selectedBillingAddress;
    const d = (addr as Addresses | null)?.address_details;
    if (!d) return "";
    return [d.zip, d.stateCode, d.city, d.addressLine1].filter(Boolean).join("|");
  }, [formik.values.isSameAsBilling, selectedBillingAddress, selectedShippingAddress]);

  React.useEffect(() => {
    if (!USE_SALES_ORDER_RESOLVE || !shippingAddressSig || !cartItems.length) return;
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    recomputeTimerRef.current = setTimeout(() => {
      void recomputeSalesRef.current?.();
    }, 300);
    return () => {
      if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    };
  }, [shippingAddressSig, cartItems.length]);

  const shippingAddrForTax =
    selectedShippingAddress ||
    selectedBillingAddress || null;

  // Wrapper function to check coupon expiration before creating order
  const onConfirmAndPay = async () => {
    if (isAnythingBusy) return;

    // âœ… Check coupon expiration first
    await checkCouponExpiration(async () => {
      // This callback runs only if coupon is valid OR user clicked OK on expiration modal
      await createDraftOrderProcess();
    });
  };

  const openCheckoutPaymentModal = (draft: { id: string; data: Record<string, unknown> }) => {
    const contact = formikValuesRef.current as Partial<InitialValues>;
    const totals = checkoutUiTotalsRef.current;
    const paymentOrder = {
      ...(isEdit && editOrder ? editOrder : order ? order : {}),
      ...draft.data,
      customer_first_name: String(
        draft.data.customer_first_name || contact.userFirstName || userInfo?.firstName || ""
      ),
      customer_last_name: String(
        draft.data.customer_last_name || contact.userLastName || userInfo?.lastName || ""
      ),
      customer_email: String(
        draft.data.customer_email || contact.userEmail || userInfo?.email || ""
      ),
      customer_phone: String(
        draft.data.customer_phone || contact.userPhone || userInfo?.phone1 || ""
      ),
      subtotal_amount: totals.subtotal,
      discount_amount: totals.discount,
      tax_amount: totals.tax,
      total_amount: totals.grandTotal,
    } as Record<string, unknown>;
    setCheckoutPaymentOrder(paymentOrder);
    setCheckoutPaymentOpen(true);
  };

  const createDraftOrderProcess = async () => {
    if (isAnythingBusy) return;
    const draft = await createDraftOrder();
    if (draft?.data) {
      openCheckoutPaymentModal({
        id: draft.id,
        data: draft.data as Record<string, unknown>,
      });
    }
  };

  const uploadOrderDocumentsToOrder = useCallback(
    async (orderDocumentId: string, files: File[]) => {
      const id = String(orderDocumentId || "").trim();
      if (!token || !id || !files.length) return;
      const fd = new FormData();
      fd.append("id", id);
      fd.append("collection_name", "sales");
      files.forEach((f) => fd.append("files[]", f));

      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const raw = await res.json().catch(() => ({}));
        const msg =
          (raw as any)?.message ||
          (raw as any)?.error ||
          `Upload failed (${res.status})`;
        throw new Error(typeof msg === "string" ? msg : "Upload failed");
      }
    },
    [token]
  );

  const createDraftOrder = async (): Promise<{ id: string; data: Record<string, unknown> } | null> => {
    setTabCount(3);
    setFormSubmitting(true); // âœ… Set busy state at start
    
    // Declare payload outside try block so it's accessible in catch block
    let payload: any = null;

    if (USE_SALES_ORDER_RESOLVE) {
      const { shippingRows, feeRows } = getLiveCheckoutChargeRows();
      const { activeShipPayload, activeFeePayload } = readLiveShipFeePayloads();
      const resolveOk = await recomputeSalesRef.current?.({
        force: true,
        shipPayload: shippingRows.length
          ? { ...(activeShipPayload ?? {}), shipping_option: shippingRows as ShipPayload["shipping_option"] }
          : activeShipPayload,
        feePayload: feeRows.length
          ? { ...(activeFeePayload ?? {}), specialfee: feeRows as FeePayload["specialfee"] }
          : activeFeePayload,
      });
      if (!resolveOk || !lastResolvedSalesRef.current) {
        showError("Could not recalculate order before checkout. Please try again.");
        setFormSubmitting(false);
        return null;
      }
    }

    try {
    // const products = cartItems?.map((item) => ({
    //   product_id: item.product_id,
    //   variant_id: item.variant_id,
    //   warehouse_id: item.warehouse_id,
    //   product_name: item.name || item.product_name,
    //   sku: item.sku,
    //   quantity: item.quantity,
    //   unit_purchase_price: item.unit_purchase_price,
    //   unit_retail_price: item.unit_retail_price,
    //   total_price: item.unit_retail_price * item.quantity,
    //   images: item.images,
    //   // âœ… Include customizations
    //   customizations: {
    //     template_name: item.customizations?.template_name || "",
    //     fields: item.customizations?.fields || [],
    //   },
    //   storage_location: item.storage_location,
    // }));

    // helper to key a row
    const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;
    const asNum = (v: any) => Number(v || 0);
    const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;

    const products = (cartItems ?? []).map((item) => {
      // const qty = Math.max(1, asNum(item.quantity));
      const baseUnit = asNum(item.unit_retail_price); // unit BEFORE discount


      const key = `${String(item.product_id)}:${String(item.variant_id)}`;
      const qty = Math.max(1, asNum(item.quantity));
      const unitBefore = asNum(item.unit_retail_price);
        const changedBefore = num(item.changed_unit_price);
      const liveSplit = (perItemCouponMap || {})[key];
      const rowDiscTotal = round2(
        typeof liveSplit === "number" ? asNum(liveSplit) : asNum(item.discount_on_total_quantity || 0)
      );

      const perUnitDisc = round2(rowDiscTotal / qty);

      // prices from the SAME discount
      // const lineBase = unitBefore * qty;
        const lineBase = changedBefore * qty;  
      const lineAfter = Math.max(0, lineBase - rowDiscTotal);
      const changedUnit = round2(lineAfter / qty);
      const item_sub_total = round2(lineBase);


      // // coupon allocation by composite key
      // const key = keyForRow({ productId: String(item.product_id), variantId: String(item.variant_id) });
      // const rowDiscTotal = round2(asNum((perRowAlloc || {})[key] || 0));

      // // line math (post-discount)
      // const lineBase = baseUnit * qty;                          // pre-discount
      // const lineAfter = Math.max(0, lineBase - rowDiscTotal);   // post-discount, pre-tax
      // const changedUnit = round2(lineAfter / qty);              // effective each after discount
      // const perUnitDisc = round2(rowDiscTotal / qty);
      // const item_sub_total = round2(changedUnit * qty);

      // Build jurisdiction details if you need them
      const taxInfoRaw = buildItemTaxInfoAtCreate(
        {
          productId: String(item.product_id),
          variantId: String(item.variant_id),
          sku: String(item.sku || ""),
          unitPrice: changedUnit,
          quantity: qty,
        },
        productTaxMap,
        shippingAddrForTax
      );

      // ðŸ”‘ Resolve live tax from TaxCloud map: SKU â†’ composite key
      const skuKey = String(item.sku || "");
      const liveRowTaxRaw =
        (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ??
        productTaxMap?.[key];
      const resolvedLineTax = Number(liveRowTaxRaw ?? NaN);

      // Final tax_amount: prefer live map, else fallback to computed/jurisdiction helper
      const tax_amount = Number.isFinite(resolvedLineTax)
        ? round2(resolvedLineTax)
        : round2(asNum((taxInfoRaw as any)?.total_product_tax_amount || 0));

      // Derive % and per-unit from the final tax_amount
      const tax_percentage = item_sub_total > 0 ? round2((tax_amount / item_sub_total) * 100) : 0;
      const unit_product_tax_amount = round2(qty ? tax_amount / qty : 0);

      const lineTaxable = readCheckoutCartLineTaxable(item);

      // Final tax block that the UI + payload will use
      const item_tax_info = {
        tax_percentage,
        unit_product_tax_amount,
        total_product_tax_amount: tax_amount,
        tax_details: (taxInfoRaw as any)?.tax_details || {},
        taxable: lineTaxable,
      };

      // Totals
      const total_price = round2(item_sub_total + tax_amount);

      const existingLineItemId = isEdit
        ? getCheckoutLineItemId(
            item as { _id?: unknown; id?: unknown; line_item_id?: unknown; product_id?: unknown; variant_id?: unknown },
            Array.isArray(editOrder?.items) ? editOrder.items : null
          )
        : "";

      return {
        ...(existingLineItemId ? { _id: existingLineItemId } : {}),
        product_id: item.product_id,
        variant_id: item.variant_id,
        warehouse_id: item.warehouse_id,
        product_name: item.name || item.product_name,
        sku: item.sku,
        is_production: item.is_production,
        is_taxable: lineTaxable,

        changed_unit_price: changedBefore,
        discount_on_unit_quantity: perUnitDisc,
        discount_on_total_quantity: rowDiscTotal,
        item_sub_total:lineBase,


        quantity: qty,
        unit_purchase_price: asNum(item.unit_purchase_price),
        unit_retail_price: round2(baseUnit),      // original unit (pre-discount)
        // changed_unit_price: round2(changedUnit),  // after discount

        // discounts per line
        // discount_on_unit_quantity: perUnitDisc,
        // discount_on_total_quantity: rowDiscTotal,

        // totals per line
        // item_sub_total,          // post-discount, pre-tax
        total_price,             // post-discount + tax
        tax_amount,              // âœ… keep a separate field too

        images: item.images,
        customizations: {
          template_name: item.customizations?.template_name || "",
          fields: item.customizations?.fields || [],
        },
        design_id: item.design_id || "",
        design_name: item.design_name || "",
        preview_url: item.preview_url || "",
        storage_location: item.storage_location,

        // âœ… tax block uses live values
        item_tax_info,
      };
    });

    const finalBillingAddress = selectedBillingAddress;
    const finalShippingAddress = formik.values.isSameAsBilling && selectedBillingAddress ? selectedBillingAddress : selectedShippingAddress;

    // shipping/special from child payloads (prefer refs â€” state may lag after resolve)
    const { shippingRows, feeRows: specialRows } = getLiveCheckoutChargeRows();

    const shippingAmount = shippingRows.reduce((s: number, r: any) => s + asNum(r.shipping_amount), 0);
    const shippingTax = shippingRows.reduce((s: number, r: any) => s + asNum(r.shipping_tax_amount), 0);
    const specialAmount = specialRows.reduce((s: number, f: any) => s + asNum(f.specialfee_amount), 0);
    const specialTax = specialRows.reduce((s: number, f: any) => s + asNum(f.specialfee_tax_amount), 0);

    // ===============================================
    // âœ… PAYLOAD: Use UI values directly (matching RealtorOrderCheckout)
    // ===============================================
    
    // Product totals from UI calculations
    const itemsSubtotal = round2((products || []).reduce((s: number, it: any) => s + asNum(it.item_sub_total), 0));
    const productTaxTotal = round2((products || []).reduce((s: number, it: any) => s + asNum(it?.item_tax_info?.total_product_tax_amount), 0));

    // Discount from UI state (preserve exact case)
    const discount_code = String(liveDiscountDisplay.code || couponInput).trim();
    const discount_amount = round2(Number(liveDiscountDisplay.amount || 0));
    const promo_code = discount_code;

    // Totals (what's shown in UI is what we send)
    const taxTotal = round2(uiTotalTax);
    const overallPreTax = round2(itemsSubtotal + shippingAmount + specialAmount);
    const grandTotal = round2(uiGrandTotal);

    // percentages (optional)
    const productTaxPct = itemsSubtotal > 0 ? (productTaxTotal / itemsSubtotal) * 100 : 0;
    const shippingTaxPct = shippingAmount > 0 ? (shippingTax / shippingAmount) * 100 : 0;
    const overallPct = overallPreTax > 0 ? (taxTotal / overallPreTax) * 100 : 0;

    const mapUiShipToApi = (r: any) => ({
      shipping_id: String(r.shipping__id ?? r.shipping_id ?? ""),
      shipping_name: String(r.shipping_name ?? ""),
      shipping_amount: asNum(r.shipping_amount),
      shipping_tax_percentage: asNum(r.shipping_tax_percentage),
      shipping_tax_amount: asNum(r.shipping_tax_amount),
      shipping_taxable: resolveShippingTaxableForPayload(r as Record<string, unknown>, shippingCosts),
      shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
      shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
    });

    const shipMeta = getSelectedShippingMeta();

      const API = process.env.REACT_APP_API_URL;

      payload = {
        buyer_type: "agent",
        signage_id: supplierId,
        signage_company_name: "",
        customer_id: userId,
        agent_id: userId,
        agent_name: userName,
        customer_first_name: formik.values.userFirstName || userInfo?.firstName || "",
        customer_last_name: formik.values.userLastName || userInfo?.lastName || "",
        customer_email: formik.values.userEmail || userInfo?.email || "",
        customer_phone: formik.values.userPhone || userInfo?.phone1 || "",
        cart_id: cartId,
        shipping_address: finalShippingAddress,
        billing_address: finalBillingAddress,
        both_address_same: formik.values.isSameAsBilling,
        items: products,
        order_type: "purchase",
        po_number: String(formik.values.po_number || "").trim(),
        status_code: 100,
        inventory_source: "agent",
        payment_method: selectedPaymentMethod,
        payment_info: null,

        tax_details: {
          tax_rate: Number(productTaxPct.toFixed(4)),
          tax_amount: Number(taxTotal.toFixed(2)),
          applied_state: String(
            (formik.values.isSameAsBilling ? selectedBillingAddress : selectedShippingAddress)?.address_details?.stateCode
            ?? (formik.values.isSameAsBilling ? selectedBillingAddress : selectedShippingAddress)?.address_details?.state
            ?? ""
          ),
        },
        shipping_method: {
          carrier_name: shipMeta.method.carrier_name,
          service_name: shipMeta.method.service_name,
          tracking_number: shipMeta.method.tracking_number,
          cost: Number(shipMeta.method.cost || 0),
          service_code: shipMeta.method.service_code,
        },
        subtotal_amount: Number(itemsSubtotal.toFixed(2)), // post-discount, pre-tax
        shipping_amount: Number(shippingAmount.toFixed(2)),
        tax_amount: Number(taxTotal.toFixed(2)),
        total_amount: Number(grandTotal.toFixed(2)),

        discount_amount: Number(discount_amount.toFixed(2)),
        discount_code,                        // â¬…ï¸ added
        promo_code,                           // â¬…ï¸ added
        order_tax_summary: {
          tax_inclusive: false,
          over_all_tax_amount: Number(taxTotal.toFixed(2)),
          over_all_tax_percentage: Number(overallPct.toFixed(4)),
          over_all_taxable_base: Number(overallPreTax.toFixed(2)),
          tax_status: editOrder?.order_tax_summary?.tax_status || "draft", // keep existing or blank
        },

        order_charges: [{
          shipping_option: (shippingRows || []).map(mapUiShipToApi),
          specialfee: (specialRows || []).map((f: any) => ({
            specialfee_id: String(f.specialfee_id ?? ""),
            specialfee_name: String(f.specialfee_name ?? ""),
            specialfee_amount: asNum(f.specialfee_amount),
            specialfee_tax_percentage: asNum(f.specialfee_tax_percentage),
            specialfee_tax_amount: asNum(f.specialfee_tax_amount),
            specialfee_taxable: resolveSpecialFeeTaxableForPayload(
              f as Record<string, unknown>,
              specialFeeCatalogRef.current
            ),
            required: resolveSpecialFeeRequiredForPayload(
              f as Record<string, unknown>,
              specialFeeCatalogRef.current
            ),
          })),
        }],
        manual_shipping_method: {
          _id: shipMeta.method.carrier_name === "Manual" ? (shippingOptions?.find(o => o.value === selectedShippingOption)?.value) : undefined,
          name: shipMeta.method.carrier_name === "Manual"
            ? `${shipMeta.method.service_name} ($${(shipMeta.method.cost ?? 0).toFixed(2)})`
            : undefined,
          cost: shipMeta.method.carrier_name === "Manual" ? shipMeta.method.cost : undefined,
        },
        notes: formik.values.notes || "",
        status_history: null,
      };

      if (USE_SALES_ORDER_RESOLVE && lastResolvedSalesRef.current) {
        payload = overlaySalesSubmitFromResolved(payload, lastResolvedSalesRef.current);
      }

      const url = isEdit ? `${API}/orders-update/${resumeOrderId}` : `${API}/orders`;
      const method = isEdit ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      // Read once, then parse if possible
      const raw = await response.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch { /* not JSON, keep raw */ }

      if (!response.ok) {
        const apiMsg = json?.message || json?.error || raw || response.statusText;
        const errorMsg = `Failed to ${isEdit ? "update" : "create"} order (${response.status}). ${apiMsg}`;
        await handleApiError(new Error(errorMsg), {
          module: "sales_order",
          action: isEdit ? "agent_order_checkout_update_order" : "agent_order_checkout_create_order",
          userId: userId || undefined,
          licenseType: license || undefined,
          userName: userName || undefined,
          url: url,
          method: method,
          requestBody: payload,
          additionalInfo: { orderId: resumeOrderId || "new" }
        }, token);
        throw new Error(errorMsg);
      }

      // Handle 204 / empty body safely
      const data = json ?? {};
      const newId = isEdit
        ? resumeOrderId
        : getOrderIdFromResponse(data);

      if (isEdit) {
        setEditOrder(data);
        showSuccess("Order updated successfully");
        return { id: String(resumeOrderId), data: data as Record<string, unknown> };
      } else {
        if (!newId) {
          safeConsole.warn("Create response did not include an id:", data);
        }
        const pendingFiles = pendingInstructionFilesRef.current.map((x) => x.file);
        if (newId && pendingFiles.length > 0) {
          try {
            await uploadOrderDocumentsToOrder(String(newId), pendingFiles);
            pendingInstructionFilesRef.current = [];
          } catch (uploadErr) {
            showError(
              uploadErr instanceof Error
                ? `Order created, but attachments failed to upload: ${uploadErr.message}`
                : "Order created, but attachments failed to upload."
            );
          }
        }
        const enrichedData = {
          ...data,
          customer_first_name:
            data.customer_first_name || formik.values.userFirstName || userInfo?.firstName || "",
          customer_last_name:
            data.customer_last_name || formik.values.userLastName || userInfo?.lastName || "",
          customer_email: data.customer_email || formik.values.userEmail || userInfo?.email || "",
          customer_phone: data.customer_phone || formik.values.userPhone || userInfo?.phone1 || "",
        };
        setOrder(enrichedData);
        setOrderId(newId);
        setInitialValues(mapOrderToForm(enrichedData));

        const orderNumber = getOrderNumberFromResponse(data);
        try {
          await linkCartToDraftOrder({
            cartId,
            orderId: String(newId),
            orderNumber,
            token,
          });
          setLinkedOrderIdFromCart(String(newId));
          setLinkedOrderNumberFromCart(orderNumber);
        } catch (linkErr) {
          safeConsole.warn("Failed to link cart to draft order:", linkErr);
        }

        showSuccess("Order created successfully");
        // Send email notifications for order placed (centralized engine)
        try {
          // if (newId && supplierId && token) {
          //   await sendSalesOrderTriggerEmail({
          //     triggerName: "Agent Sales Order Placed",
          //     orderId: String(newId),
          //     signageId: String(supplierId),
          //     token: token || "",
          //     fallbackUserName:
          //       userInfo?.firstName && userInfo?.lastName
          //         ? `${userInfo.firstName} ${userInfo.lastName}`
          //         : userInfo?.firstName || userName || "",
          //   });
          // }

          const signageCompanyName = "Signage Company";
          // âœ… Trigger: Sales Order Created - Send notifications
          try {
            const { createNotification } = await import("../../services/notificationApi");
            const { getSalesOrderCreatedContent } = await import("../../services/notificationContent");

            // Send notification to Agent
            if (userId && token) {
              const agentContent = getSalesOrderCreatedContent({
                orderId: orderNumber,
                signageCompanyName,
                recipientType: "agent",
              });
              await createNotification(userId, agentContent, token, "agent");
            }

            // Send notification to Signage (supplier)
            if (supplierId && token) {
              const agentName = userInfo?.firstName && userInfo?.lastName
                ? `${userInfo.firstName} ${userInfo.lastName}`
                : userInfo?.email?.split("@")[0] || "Agent";

              const signageContent = getSalesOrderCreatedContent({
                orderId: orderNumber,
                enteredBy: agentName,
                recipientType: "signage",
              });
              await createNotification(supplierId, signageContent, token, "signage");
            }
          } catch (notifError) {
            safeConsole.warn("Failed to send sales order created notification:", notifError);
          }
        } catch (emailError) {
          safeConsole.warn("Failed to send order placed email:", emailError);
        }

        return { id: String(newId), data: enrichedData as Record<string, unknown> };
      }
    } catch (error) {
      safeConsole.error("Error to create order:", error);
      showError(error instanceof Error ? error.message : "Failed to create order");
      // Send error email notification with payload if available
      // Note: payload might not be available if error occurs after order creation
      try {
        await handleApiError(error, {
          module: "sales_order",
          action: isEdit ? "agent_order_checkout_update_order_post_save" : "agent_order_checkout_create_order_post_save",
          userId: userId || undefined,
          licenseType: license || undefined,
          userName: userName || undefined,
          requestBody: payload,
          url: isEdit ? `${process.env.REACT_APP_API_URL}/orders-update/${resumeOrderId}` : `${process.env.REACT_APP_API_URL}/orders`,
          method: isEdit ? "PUT" : "POST",
          additionalInfo: { orderId: resumeOrderId || "new", errorPhase: "post_save" }
        }, token);
      } catch (errorHandlerErr) {
        safeConsole.warn("Failed to log error:", errorHandlerErr);
      }
      throw error;
    } finally {
      setFormSubmitting(false);
    }
    return null;
  };


  const completeOrder = async (getmethod: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            payment_id: "",
            total_amount: totalAmount,
            amount_paid: 0,
            amount_due: totalAmount,
            payment_date: new Date().toISOString(),
            method: getmethod,
          }),
        }
      );

      if (!response.ok) {
        const errorMsg = "Failed to complete the order";
        await handleApiError(new Error(errorMsg), {
          module: "sales_order",
          action: "agent_order_checkout_complete_order_payment",
          userId: userId || undefined,
          url: `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`,
          method: "PUT",
          additionalInfo: { orderId, method: getmethod }
        }, token);
        throw new Error(errorMsg);
      }

      await response.json().catch(() => ({}));

      showSuccess("Order placed successfully!");

      // âœ… Trigger 3: Agent Sales Order Placed - Send notifications
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getAgentSalesOrderPlacedContent } = await import("../../services/notificationContent");
        const { fetchUserData } = await import("../../utils/userDataFetcher");
        
        // Fetch order data to get order details
        const orderResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          const orderNumber = orderData?.order_number || orderId;

          // Get supplier name
          const orderSupplierId = orderData?.signage_id || supplierId;
          let supplierName = "Supplier";
          if (orderSupplierId) {
            try {
              const supplierData = await fetchUserData(orderSupplierId, token || "");
              supplierName = supplierData?.companyName || supplierData?.firmDetails?.companyName || supplierName;
            } catch (err) {
              safeConsole.warn("Failed to fetch supplier data:", err);
            }
          }

          // Get agent name
          let agentName = "Agent";
          if (userId) {
            try {
              const agentData = await fetchUserData(userId, token || "");
              agentName = `${agentData?.firstName || ""} ${agentData?.lastName || ""}`.trim() || agentName;
            } catch (err) {
              safeConsole.warn("Failed to fetch agent data:", err);
            }
          }

          // Notify Agent
          if (userId && token) {
            const agentContent = getAgentSalesOrderPlacedContent({
              orderId: orderNumber,
              supplierName,
              agentName,
              recipientType: "agent",
            });
            await createNotification(userId, agentContent, token, "agent");
          }

          // Notify Signage Admin/Team Members
          if (orderSupplierId) {
            try {
              const signageTeamResp = await fetch(`${process.env.REACT_APP_API_URL}/users-search/datatable`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  page: 1,
                  limit: 100,
                  filters: {
                    signage_company_id: orderSupplierId,
                    licenseType: ["SIGNAGE_FIRM", "SIGNAGE_TEAM_MEMBER"],
                    status: "Active",
                  },
                  fields: ["id", "email", "licenseType"],
                }),
              });

              if (signageTeamResp.ok) {
                const signageTeamData = await signageTeamResp.json();
                const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

                for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                  const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                  if (memberId && token) {
                    try {
                      const signageContent = getAgentSalesOrderPlacedContent({
                        orderId: orderNumber,
                        supplierName,
                        agentName,
                        recipientType: memberRecipientType,
                      });
                      await createNotification(memberId, signageContent, token, memberRecipientType);
                    } catch (err) {
                      safeConsole.warn(`Failed to send notification to signage team member ${memberId}:`, err);
                    }
                  }
                }
              }
            } catch (signageErr) {
              safeConsole.warn("Failed to fetch signage team members for notification:", signageErr);
            }
          }
        }
      } catch (notifError) {
        safeConsole.warn("Failed to send order placed notification:", notifError);
        // Don't block the order completion if notification fails
      }

      // Send email notifications for order placed (centralized engine)
      // try {
      //   if (orderId && supplierId && token) {
      //     await sendSalesOrderTriggerEmail({
      //       triggerName: "Agent Sales Order Placed",
      //       orderId: String(orderId),
      //       signageId: String(supplierId),
      //       token: token || "",
      //       fallbackUserName:
      //         userInfo?.firstName && userInfo?.lastName
      //           ? `${userInfo.firstName} ${userInfo.lastName}`
      //           : userInfo?.firstName || userName || "",
      //     });
      //   }
      // } catch (emailError) {
      //   safeConsole.warn("Failed to send order placed email:", emailError);
      // }
    } catch (error) {
      const errMessage =
        (error as Error).message || "An unknown error occurred";
      safeConsole.error("Order Failed:", errMessage);
      showError(`Error: ${errMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const fetchGroups = async () => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/customer-groups`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch groups.");
  //       }
  //       const data = await response.json();
  //       setGroups(data);
  //     } catch (err) {
  //       showError("Failed to load groups.");
  //       setError(
  //         err instanceof Error ? err.message : "An unknown error occurred"
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchGroups();
  // }, [token]);

  const handleQuantityChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;

    setCartItems((prevItems) =>
      prevItems.map((item, i) =>
        i === index ? { ...item, quantity: value } : item
      )
    );
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setPaymentMethod(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [token]);

  useEffect(() => {
    const fetchShippingCosts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/shipping-costs?user_id=${supplierId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch shipping costs: ${response.statusText}`);
        }
        const data = await response.json();
        setShippingCosts(data);
      } catch (err) {
        await handleApiError(err, {
          module: "sales_order",
          action: "agent_order_checkout_fetch_shipping_costs",
          userId: userId || undefined,
          url: `${process.env.REACT_APP_API_URL}/shipping-costs?user_id=${supplierId}`,
          method: "GET",
          additionalInfo: { supplierId }
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchShippingCosts();
  }, [supplierId]);
  useEffect(() => {
    const fetchPaymentIntent = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${supplierId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              amount: Math.round(Number(totalAmount) * 100), // âœ… ensures exact integer
              currency: "usd",
              order_id: String(order?.id || orderId || ""),
              order_number: String(order?.order_number || ""),
              customer_name: `${order?.customer_first_name || formik.values.userFirstName || ""} ${order?.customer_last_name || formik.values.userLastName || ""}`.trim(),
              customer_email: String(order?.customer_email || formik.values.userEmail || ""),
              customer_phone: String(order?.customer_phone || formik.values.userPhone || ""),
              payment_method_types: ["card"],
            }),
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to create PaymentIntent: ${res.statusText}`);
        }
        const data = await res.json();
        setClientSecret(data.clientSecret || data.client_secret);
      } catch (err) {
        // await handleApiError(err, {
        //   module: "sales_order",
        //   action: "agent_order_checkout_create_payment_intent",
        //   userId: userId || undefined,
        //   url: `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${supplierId}`,
        //   method: "POST",
        //   additionalInfo: { supplierId, amount: totalAmount }
        // }, token);
        safeConsole.error("Failed to create PaymentIntent:", err);
      }
    };

    if (stripePromise) fetchPaymentIntent();
  }, [stripePromise, totalAmount, supplierId, token, order, orderId, formik.values.userFirstName, formik.values.userLastName, formik.values.userEmail, formik.values.userPhone]);

  const idOf = (x?: any): string => {
    if (!x) return "";
    if (typeof x === "string") return x;
    if (typeof x?.url === "string") return x.url;
    return "";
  };
  const allImageIds = React.useMemo(() => {
    const ids = new Set<string>();
    (cartItems || []).forEach((row) => {
      // row.images may be: string[] | {url}[]
      const firstId = idOf(row?.images?.[0]);
      if (firstId) ids.add(firstId);
      if (row?.design_id && typeof row?.preview_url === "string" && row.preview_url.trim()) {
        ids.add(row.preview_url.trim());
      }
      const fields = row?.customizations?.fields || [];
      fields.forEach((f: any) => {
        if (String(f?.field_type || "").toLowerCase() !== "file") return;
        const raw = f?.value;
        const fid =
          typeof raw === "string"
            ? raw.trim()
            : raw && typeof raw === "object" && raw.url
              ? String(raw.url).trim()
              : "";
        if (fid) ids.add(fid);
      });
    });

    return Array.from(ids);
  }, [cartItems]);

  const pickerStudioBatchVariantPreviews = useMemo(
    () => studioBatchVariantPreviewsFromCartLines(cartItems || []),
    [cartItems]
  );

  const dataForMap = React.useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      {
        product: {
          variants: [
            { images: allImageIds.map((url) => ({ url })) },
          ],
        },
      },
    ];
  }, [allImageIds]);

  // presigned urls map
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    const cleaned = raw.replace(/\\/g, "/");
    if (/^https?:\/\//i.test(cleaned)) return cleaned;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = cleaned.replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };

  const mobileColumns: ColumnDef<CartItemRow, any>[] = [
    ch.display({
      id: "product",
      header: () => "Product",
      cell: ({ row }) => {
        const item = row.original;
        const fields = item?.customizations?.fields || [];
        const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
        const firstFileVal = (raw: any): string => {
          if (!raw) return "";
          if (typeof raw === "string") return raw.trim();
          if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
          return "";
        };
        const hasValue = (f: any): boolean => {
          if (isFile(f)) return !!firstFileVal(f?.value);
          return String(f?.value ?? "").trim().length > 0;
        };
        const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
        const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));
        const hasAnyCustom = textFields.length > 0 || fileFields.length > 0;
        const title = item.name || item.product_name;
        const firstId = idOf(item?.images?.[0]);
        const imgSrc =
          (item?.design_id && item?.preview_url
            ? getImageSrc(item.preview_url, imageUrlMap) || buildImageUrl(item.preview_url)
            : "") ||
          getImageSrc(firstId, imageUrlMap) ||
          getImageSrc(item?.image_url, imageUrlMap) ||
          buildImageUrl(firstId || item?.image_url) ||
          "";
        const imgOk =
          !!imgSrc &&
          imgSrc.trim() !== "" &&
          !imgSrc.endsWith("/undefined") &&
          !imgSrc.includes("/null") &&
          !imgSrc.endsWith("/");
        const showSrc = imgOk ? imgSrc : "";

        return (
          <div className="d-flex gap-3">
            <div style={{ width: 72, height: 72, flexShrink: 0 }}>
              <img
                src={showSrc || imagecustomRounded}
                alt=""
                className="img-fluid rounded"
                style={{ objectFit: "contain", width: "100%", height: "100%" }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                }}
              />
            </div>
            <div>
              <div className="fw-semibold text-uppercase mb-1 wrap-anywhere">{title}</div>
              <div className="text-muted small">{item.sku}</div>

              {hasAnyCustom ? (
              <div className="mt-2 custom-fields-container">
                {textFields.map((field: any, idx: number) => (
                  <div key={`cf-t-${field?._id || field?.field_name || idx}`} className="mb-1" style={{ wordBreak: "break-word", maxWidth: 380 }}>
                    <strong className="custom-cells wrap-anywhere">{field.field_name}:</strong>{" "}
                    <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                  </div>
                ))}
                {fileFields.map((field: any, idx: number) => {
                  const fid = firstFileVal(field?.value);
                  const href = getImageSrc(fid, imageUrlMap) || buildImageUrl(fid);
                  return (
                    <div key={`cf-f-${field?._id || field?.field_name || idx}`} className="mb-2" style={{ wordBreak: "break-word", maxWidth: 380 }}>
                      <div className="text-muted small wrap-anywhere">{field.field_name}</div>
                      {href ? (
                        <a href={href} download target="_blank" rel="noopener noreferrer">
                          <img
                            src={href}
                            alt={field.field_name}
                            className="rounded"
                            style={{ maxWidth: 88, height: "auto", cursor: "pointer" }}
                          />
                        </a>
                      ) : /^[0-9a-f]{24}$/i.test(fid) ? (
                        <span className="text-muted small">Loadingâ€¦</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              ) : null}
            </div>
          </div>
        );
      },
    }),

    // Quantity (editable)
    ch.display({
      id: "qty",
      header: () => "Quantity",
      cell: ({ row }) => {
        const i = row.index;
        return (
          <input
            type="number"
            className="form-control text-center"
            min={1}
            value={row.original.quantity || 1}
            onChange={(e) => handleQuantityChange(i, e)}
            disabled
          />
        );
      },
    }),

    ch.display({
      id: "unit_price",
      header: () => "Unit Price",
      cell: ({ row }) => {
        const item = row.original;
        const unitRetail = Number(item.unit_retail_price || 0) ||
          Math.round((Number(item.changed_unit_price || 0) + Number(item.discount_on_unit_quantity || 0)) * 100) / 100;
        return `$${unitRetail.toFixed(2)}`;
      },
    }),

    ch.display({
      id: "changed_price",
      header: () => "Changed Price",
      cell: ({ row }) => {
        const item = row.original;
        const qty = Math.max(1, Number(item.quantity || 1));
        const unitRetail = Number(item.unit_retail_price || item.unit_purchase_price || 0);
        const changedUnit = Number(item.changed_unit_price ?? unitRetail);
        return `$${changedUnit.toFixed(2)}`;
      },
    }),

    ch.display({
      id: "discount",
      header: () => "Discount",
      cell: ({ row }) => {
        const item = row.original;
        const key = `${item.product_id}:${item.variant_id}`;
        const qty = Math.max(1, Number(item.quantity || 1));
        const unitRetail = Number(item.unit_retail_price || item.unit_purchase_price || 0);
        const changedUnit = Number(item.changed_unit_price ?? unitRetail);
        const { lineDiscount: rowDiscount } = getAgentRealtorCheckoutLinePricing({
          item,
          qty,
          unitRetail,
          changedUnitPrice: changedUnit,
          lineKey: key,
        });
        return <DiscountAmountDisplay amount={rowDiscount} />;
      },
    }),

    {
      id: "line_tax",
      header: () => "Tax",
      cell: ({ row }) => {
        const item = row.original;
        const key = `${item.product_id}:${item.variant_id}`;
        const skuKey = String(item.sku || "");
        const taxRaw = (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ?? productTaxMap?.[key];
        const itemTaxInfo = item?.item_tax_info;
        const savedTax = Array.isArray(itemTaxInfo)
          ? 0
          : Number((itemTaxInfo as { total_product_tax_amount?: number })?.total_product_tax_amount || 0);
        const tax = Number.isFinite(taxRaw)
          ? Number(taxRaw || 0)
          : Number(item?.tax_details?.tax_amount || savedTax);
        return `$${tax.toFixed(2)}`;
      },
    },

    {
      id: "total",
      header: () => "Total Amount",
      cell: ({ row }) => {
        const item = row.original;
        const key = `${item.product_id}:${item.variant_id}`;
        const skuKey = String(item.sku || "");
        const qty = Math.max(1, Number(item.quantity || 1));
        const unitRetail = Number(item.unit_retail_price || item.unit_purchase_price || 0);
        const changedUnit = Number(item.changed_unit_price ?? unitRetail);
        const taxRaw = (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ?? productTaxMap?.[key];
        const itemTaxInfo = item?.item_tax_info;
        const savedTax = Array.isArray(itemTaxInfo)
          ? 0
          : Number((itemTaxInfo as { total_product_tax_amount?: number })?.total_product_tax_amount || 0);
        const tax = Number.isFinite(taxRaw)
          ? Number(taxRaw || 0)
          : Number(item?.tax_details?.tax_amount || savedTax);
        const { lineTotal } = getAgentRealtorCheckoutLinePricing({
          item,
          qty,
          unitRetail,
          changedUnitPrice: changedUnit,
          lineKey: key,
          rowTax: tax,
        });
        return `$${lineTotal.toFixed(2)}`;
      },
    },

    // Actions
    // ch.display({
    //   id: "actions",
    //   header: () => "Action",
    //   cell: ({ row }) => {
    //     const i = row.index;
    //     return (
    //       <div className="d-flex justify-content-end gap-2">
    //         {i === cartItems.length - 1 && (
    //           <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setPickerOpen(true)}>
    //             <i className="ri-add-line" /> Add
    //           </button>
    //         )}
    //         {cartItems.length > 1 && (
    //           <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeProductRow(i)}>
    //             <i className="ri-close-line" /> Remove
    //           </button>
    //         )}
    //       </div>
    //     );
    //   },
    // }),
  ];

  const mobileData = useMemo<CartItemRow[]>(() => cartItems, [cartItems]);
  const mobileTable = useReactTable({
    data: mobileData,
    columns: mobileColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // âœ… minimal, TS-safe, easy to read
  const hasEmail = !!formik.values.userEmail?.trim();
  const hasPhone = !!formik.values.userPhone?.trim();
  const hasBilling = !!formik.values.billingAddressId;
  const hasShipping = formik.values.isSameAsBilling ? hasBilling : !!formik.values.shippingAddressId;

  // âœ… Validation: Check if shipping option is selected
  const hasShippingOption = React.useMemo(() => {
    const shippingOptions = shipPayload?.shipping_option || [];
    return shippingOptions.length > 0;
  }, [shipPayload]);

  // âœ… Validation: Check if all required special fees are selected
  const hasRequiredSpecialFees = React.useMemo(() => {
    const specialFees = feePayload?.specialfee || [];
    const selectedFeeIds = feePayload?.selected_fee_ids || [];
    const requiredFees = specialFees.filter((f: any) => f.required === true);

    if (requiredFees.length === 0) return true; // No required fees
    return requiredFees.every((rf: any) => selectedFeeIds.includes(rf.specialfee_id));
  }, [feePayload]);

  // âœ… Final validation: all required fields + not busy
  const canSaveOrder = React.useMemo(() => {
    return hasEmail && hasPhone && hasBilling && hasShipping && hasShippingOption && !isAnythingBusy;
  }, [hasEmail, hasPhone, hasBilling, hasShipping, hasShippingOption, isAnythingBusy]);

  // Debug log for validation state
  React.useEffect(() => {
    safeConsole.log("ðŸ” Button Validation State:", {
      canSaveOrder,
      breakdown: {
        hasEmail,
        hasPhone,
        hasBilling,
        hasShipping,
        hasShippingOption,
        isAnythingBusy,
      },
      values: {
        userEmail: formik.values.userEmail || "NOT SET",
        userPhone: formik.values.userPhone || "NOT SET",
        billingAddressId: formik.values.billingAddressId || "NOT SET",
        shippingAddressId: formik.values.shippingAddressId || "NOT SET",
        isSameAsBilling: formik.values.isSameAsBilling,
        shippingOptionsCount: shipPayload?.shipping_option?.length || 0,
      },
      busyStates,
    });
  }, [hasEmail, hasPhone, hasBilling, hasShipping, hasShippingOption, isAnythingBusy, canSaveOrder, formik.values.userEmail, formik.values.userPhone, formik.values.billingAddressId, formik.values.shippingAddressId, formik.values.isSameAsBilling, shipPayload, busyStates]);

  // Safety mechanism: Reset stuck busy states after timeout
  React.useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    
    Object.entries(busyStates).forEach(([key, isBusy]) => {
      if (isBusy) {
        const timeout = setTimeout(() => {
          safeConsole.warn(`âš ï¸ ${key} busy state stuck for >5s, forcing reset`);
          setBusyStates(prev => ({ ...prev, [key]: false }));
        }, 5000); // 5 seconds timeout
        timers.push(timeout);
      }
    });
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [busyStates.specialFee, busyStates.shipping, busyStates.productTax, busyStates.mainForm]);

  React.useEffect(() => {
    if (!editOrder?.id) return;
    const shipRowsInit = getShippingRowsFromOrder(editOrder);
    if (!shipPayload?.shipping_option?.length && shipRowsInit.length) {
      const selection = getInitialShippingSelectionFromOrder(editOrder);
      const isUps = Boolean(selection?.ups_service_code);
      const nextShip = {
        shipping_option: shipRowsInit,
        isUps,
        isManual: !isUps,
        selected_ids: selection
          ? {
              manual_shipping_id: selection.manual_shipping_id,
              ups_service_code: selection.ups_service_code,
              integration_id: selection.integration_id,
              shipper_address_id: selection.shipper_address_id,
            }
          : undefined,
      } as ShipPayload;
      shipPayloadRef.current = nextShip;
      setShipPayload(nextShip);
    }
  }, [editOrder?.id, shipPayload?.shipping_option?.length]);

  // hydrate SpecialFee from edit order once
  React.useEffect(() => {
    if (!editOrder?.id) return;
    const feeRowsInit = getSpecialFeeRowsFromOrder(editOrder);
    if (!feePayload?.specialfee?.length && feeRowsInit.length) {
      const feeIds = feeRowsInit
        .map((r: any) => String(r.specialfee_id ?? ""))
        .filter(Boolean);
      const nextFee = {
        specialfee: feeRowsInit,
        selected_fee_ids: feeIds,
        total_amount: 0,
        total_tax_amount: 0,
        total_tax_percentage: 0,
      } as FeePayload;
      feePayloadRef.current = nextFee;
      setFeePayload(nextFee);
    }
  }, [editOrder?.id, feePayload?.specialfee?.length]);

  function computeSavedTotals(o: any) {
    const ch = (o?.order_charges?.[0] ?? {}) as any;
    const ship = Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
    const fee = Array.isArray(ch?.specialfee) ? ch.specialfee : [];

    const sub = n(o?.subtotal_amount ?? o?.pricing?.sub_total ??
      (Array.isArray(o?.items) ? o.items.reduce((s: number, it: any) => s + n(it?.total_price ?? n(it?.unit_retail_price) * n(it?.quantity)), 0) : 0));

    const prodTax = Array.isArray(o?.items) ? o.items.reduce((s: number, it: any) => s + n(it?.item_tax_info?.total_product_tax_amount), 0) : 0;
    const shipTax = ship.reduce((s: any, r: any) => s + n(r?.shipping_tax_amount), 0);
    const feeTax = fee.reduce((s: any, r: any) => s + n(r?.specialfee_tax_amount), 0);

    const taxTot = n(o?.order_tax_summary?.over_all_tax_amount ?? (prodTax + shipTax + feeTax));
    const shipAmt = ship.reduce((s: any, r: any) => s + n(r?.shipping_amount), 0);
    const feeAmt = fee.reduce((s: any, r: any) => s + n(r?.specialfee_amount), 0);
    const grand = n(o?.total_amount ?? o?.pricing?.grand_total ?? (sub + shipAmt + feeAmt + taxTot));
    // safeConsole.log("computeSavedTotals:", { sub, shipAmt, feeAmt, prodTax, shipTax, feeTax, taxTot, grand, ship, fee });
    return {
      subTotal: sub, shipping: shipAmt, specialFee: feeAmt,
      taxBreakdown: { product: prodTax, shipping: shipTax, specialFee: feeTax },
      taxTotal: taxTot, grandTotal: grand,
      _savedShipRows: normShip(ship), _savedFeeRows: normFee(fee),
    };
  }
  const productsKey = React.useMemo(() =>
    JSON.stringify(
      cartItems.map((i) => [
        i.product_id, i.variant_id,
        Number(i.quantity || 0), Number(i.unit_retail_price || 0),
      ])
    ),
    [cartItems]);

  const [savedTotals, setSavedTotals] = React.useState<any>(null);

  // snapshot once when editOrder is loaded
  React.useEffect(() => { if (editOrder && !savedTotals) setSavedTotals(computeSavedTotals(editOrder)); }, [editOrder, savedTotals]);

  React.useEffect(() => {
    if (!savedTotals) return;
    const curShip = normShip(shipPayload?.shipping_option ?? []);
    const curFee = normFee(feePayload?.specialfee ?? []);
    const changed =
      JSON.stringify(savedTotals._savedShipRows) !== JSON.stringify(curShip) ||
      JSON.stringify(savedTotals._savedFeeRows) !== JSON.stringify(curFee);
    if (changed) setUseLiveTotals(true);
  }, [savedTotals, shipPayload?.shipping_option, feePayload?.specialfee]);

  // destination used for taxes (shipping if present, else billing)
  const taxAddressObj = React.useMemo(
    () => (formik.values.isSameAsBilling ? selectedBillingAddress : selectedShippingAddress) || selectedBillingAddress,
    [formik.values.isSameAsBilling, selectedBillingAddress, selectedShippingAddress]
  );

  const selectedProductsWithDims = useMemo(() => {
    const items = asArray(cartItems);
    const products: ProductObj[] = asArray(productobj) as ProductObj[]; // <-- normalize
    return items.map((ci: any) => {
      const d = ci?.package_dimensions ?? ci?.package_dimensions ?? {};
      const dims = {
        length: Number(d.length ?? 0) || 0,
        width: Number(d.width ?? 0) || 0,
        height: Number(d.height ?? 0) || 0,
        weight: Number(d.weight ?? 0) || 0,
        weight_type: String(d.weight_type ?? "LBS"),
      };

      return {
        productId: ci.product_id,
        variantId: ci.variant_id,
        quantity: Number(ci.quantity ?? 0) || 0,
        package_dimensions: dims,
      };
    });
  }, [cartItems, productobj]);

  const shippingProductsKey = useMemo(() => JSON.stringify(
    selectedProductsWithDims.map((p: any) => ({
      productId: p.productId,
      variantId: p.variantId,
      quantity: Number(p.quantity || 0),
      length: Number(p.package_dimensions?.length || 0),
      width: Number(p.package_dimensions?.width || 0),
      height: Number(p.package_dimensions?.height || 0),
      weight: Number(p.package_dimensions?.weight || 0),
      weightType: String(p.package_dimensions?.weight_type || "LBS"),
    }))
  ), [selectedProductsWithDims]);
  const keyFor = (o: { product_id?: string; variant_id?: string; productId?: string; variantId?: string }) =>
    `${String(o.product_id ?? o.productId ?? "")}:${String(o.variant_id ?? o.variantId ?? "")}`;

  const hasZeroOrNegativeLine = React.useMemo(() => {
    return (cartItems || []).some((ci) => {
      const key = keyFor(ci);
      const qty = Number(ci.quantity || 0);
      if (qty <= 0) return true;
      // const base = Number(ci.unit_retail_price || 0) * qty;
      const base = Number(ci.changed_unit_price || 0) * qty;
      const disc = Number(perItemCouponMap[key] || 0);
      const net = base - disc;
      return net <= 0;
    });
  }, [cartItems, perItemCouponMap]);




  // Friendly message (shown under table) and you can also reuse this to disable submit
  const zeroOrNegativeMsg =
    hasZeroOrNegativeLine
      ? "Subtotal cannot be zero or negative after discount. Please adjust quantity or unit price, or remove the coupon."
      : null;


  // shipping/special-fee amounts/taxes from child payloads
  const shippingAmount = React.useMemo(() => {
    const rows = Array.isArray(shipPayload?.shipping_option) ? shipPayload!.shipping_option! : [];
    return rows.reduce((s, r) => s + Number(r?.shipping_amount || 0), 0);
  }, [shipPayload?.shipping_option]);

  const shippingTaxAmount = React.useMemo(() => {
    const rows = Array.isArray(shipPayload?.shipping_option) ? shipPayload!.shipping_option! : [];
    return rows.reduce((s, r) => s + Number(r?.shipping_tax_amount || 0), 0);
  }, [shipPayload?.shipping_option]);

  const specialFeeAmount = React.useMemo(() => {
    const rows = Array.isArray(feePayload?.specialfee) ? feePayload!.specialfee! : [];
    return rows.reduce((s, r) => s + Number(r?.specialfee_amount || 0), 0);
  }, [feePayload?.specialfee]);

  const specialFeeTaxAmount = React.useMemo(() => {
    const rows = Array.isArray(feePayload?.specialfee) ? feePayload!.specialfee! : [];
    return rows.reduce((s, r) => s + Number(r?.specialfee_tax_amount || 0), 0);
  }, [feePayload?.specialfee]);
  // ---------- Order Totals VM (server defaults â†’ live when available) ----------
  const num = (v: any) => Number(v || 0);

  // Use the loaded order when editing; otherwise the newly created order
  const sourceOrder = editOrder || order;

  // SERVER totals (from GET/DB)
  const srvSubProducts = num(sourceOrder?.subtotal_amount);
  const srvTotalAmount = num(sourceOrder?.total_amount);
  const srvDiscountTotal = num(sourceOrder?.discount_amount || cartHeader?.discount_amount);

  console.log("srvDiscountTotal", srvDiscountTotal);
  const srvTaxOverall = num(sourceOrder?.order_tax_summary?.over_all_tax_amount);

  // SERVER charges
  const srvShipRows = Array.isArray(sourceOrder?.order_charges?.[0]?.shipping_option)
    ? sourceOrder!.order_charges![0]!.shipping_option! : [];
  const srvFeeRows = Array.isArray(sourceOrder?.order_charges?.[0]?.specialfee)
    ? sourceOrder!.order_charges![0]!.specialfee! : [];

  const srvShipAmt = srvShipRows.reduce((s: number, r: any) => s + num(r.shipping_amount), 0);
  const srvShipTax = srvShipRows.reduce((s: number, r: any) => s + num(r.shipping_tax_amount), 0);
  const srvFeeAmt = srvFeeRows.reduce((s: number, r: any) => s + num(r.specialfee_amount), 0);
  const srvFeeTax = srvFeeRows.reduce((s: number, r: any) => s + num(r.specialfee_tax_amount), 0);

  // LIVE from UI/engines/children (already managed in this file)
  const liveSubProducts = num(productTotals?.subTotal);
  const liveProdTax = num(productTotals?.productTax);
  const liveShipAmt = num(shippingAmount);
  const liveShipTax = num(shippingTaxAmount);
  const liveFeeAmt = num(specialFeeAmount);
  const liveFeeTax = num(specialFeeTaxAmount);

  // Switch to LIVE numbers once any live source is non-zero
  const usingLive =
    (liveSubProducts || liveProdTax || liveShipAmt || liveShipTax || liveFeeAmt || liveFeeTax) > 0;

  // Subtotal (products, post-discount, pre-tax)
  const subTotalProducts = usingLive ? liveSubProducts : srvSubProducts;

  // Charges to show
  const shippingAmountToShow = usingLive ? liveShipAmt : srvShipAmt;
  const specialFeeAmountToShow = usingLive ? liveFeeAmt : srvFeeAmt;

  // Tax breakdown to show
  const productTaxToShow = usingLive ? liveProdTax : num(srvTaxOverall - srvShipTax - srvFeeTax);
  const shippingTaxToShow = usingLive ? liveShipTax : srvShipTax;
  const feeTaxToShow = usingLive ? liveFeeTax : srvFeeTax;

  // Total tax
  const totalTaxAll = num(productTaxToShow) + num(shippingTaxToShow) + num(feeTaxToShow);

  // ===============================================
  // âœ… SIMPLIFIED UI TOTALS - What you see is what you send (matching RealtorOrderCheckout)
  // ===============================================
  
  // 1ï¸âƒ£ Subtotal (before discount) = Sum of (qty Ã— unit_retail_price)
  const uiSubtotal = React.useMemo(() => {
    return round2((cartItems || []).reduce((sum, item) => {
      return sum + (num(item.quantity) * num(item.changed_unit_price));
    }, 0));
  }, [cartItems]);
  
  // 2ï¸âƒ£ Total Discount from resolve
  const uiDiscount = React.useMemo(() => {
    return round2(Number(liveDiscountDisplay.amount || 0));
  }, [liveDiscountDisplay.amount, resolvedTotalsTick]);
  
  // 3ï¸âƒ£ Shipping & Special Fee (from child components)
  const uiShipping = num(shippingAmountToShow || 0);
  const uiShippingTax = num(shippingTaxToShow || 0);
  const uiSpecialFee = num(specialFeeAmountToShow || 0);
  const uiSpecialFeeTax = num(feeTaxToShow || 0);
  
  // 4ï¸âƒ£ Product Tax (from tax engine on discounted amounts)
  const uiProductTax = num(productTaxToShow || 0);
  
  // 5ï¸âƒ£ Total Tax
  const uiTotalTax = React.useMemo(() => {
    const fromResolved = lastResolvedSalesRef.current?.order_tax_summary?.over_all_tax_amount;
    if (fromResolved != null && Number.isFinite(Number(fromResolved))) {
      return round2(Number(fromResolved));
    }
    const fromBreakdown =
      Number(taxBreakdown.product || 0) +
      Number(taxBreakdown.shipping || 0) +
      Number(taxBreakdown.specialFee || 0);
    if (fromBreakdown > 0) return round2(fromBreakdown);
    return round2(uiProductTax + uiShippingTax + uiSpecialFeeTax);
  }, [
    uiProductTax,
    uiShippingTax,
    uiSpecialFeeTax,
    taxBreakdown,
    resolvedTotalsTick,
  ]);
  
  // 6ï¸âƒ£ Grand Total = subtotal - discount + shipping + special fee + all taxes
  const uiGrandTotal = round2(
    uiSubtotal - uiDiscount + uiShipping + uiSpecialFee + uiTotalTax
  );

  checkoutUiTotalsRef.current = {
    grandTotal: uiGrandTotal,
    discount: uiDiscount,
    subtotal: uiSubtotal,
    tax: uiTotalTax,
  };

  const shippingRowsForTotalsLabel = React.useMemo(() => {
    const live = shipPayload?.shipping_option;
    if (Array.isArray(live) && live.length > 0) return live;
    const ch = sourceOrder?.order_charges?.[0] as any;
    return Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
  }, [shipPayload?.shipping_option, sourceOrder?.order_charges]);

  const isUpsShippingForTotalsLabel = React.useMemo(() => {
    const live = shipPayload?.shipping_option;
    if (Array.isArray(live) && live.length > 0) return !!shipPayload?.isUps;
    const ch = sourceOrder?.order_charges?.[0] as any;
    const rows = Array.isArray(ch?.shipping_option) ? ch.shipping_option : [];
    const so = rows[0] as any;
    const rawId = String(so?.shipping_id ?? so?.shipping__id ?? "");
    if (rawId && /^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual") return true;
    return /^ups$/i.test(String(so?.shipping_type ?? "").trim());
  }, [shipPayload?.shipping_option, shipPayload?.isUps, sourceOrder?.order_charges]);

  const specialFeeRowsForTotalsLabel = React.useMemo(() => {
    const live = feePayload?.specialfee;
    if (Array.isArray(live) && live.length > 0) return live;
    const ch = sourceOrder?.order_charges?.[0] as any;
    return Array.isArray(ch?.specialfee) ? ch.specialfee : [];
  }, [feePayload?.specialfee, sourceOrder?.order_charges]);

  const shippingChargeTotalsLabel = React.useMemo(
    () => formatShippingChargeOrderTotalsLabel(shippingRowsForTotalsLabel, { isUps: isUpsShippingForTotalsLabel }),
    [shippingRowsForTotalsLabel, isUpsShippingForTotalsLabel]
  );
  const specialFeeBreakdownDisplay = React.useMemo(
    () => buildSpecialFeeCostBreakdownDisplay(specialFeeRowsForTotalsLabel),
    [specialFeeRowsForTotalsLabel]
  );

  // ---- Coupon expiration check ----
  const couponProducts = React.useMemo(
    () => (cartItems || []).map(it => ({
      product_id: String(it.product_id || ""),
      variant_id: String(it.variant_id || ""),
    })),
    [cartItems]
  );

  const { checkAndProceed: checkCouponExpiration, checking: checkingCoupon, Modal: CouponExpirationModal } = useCouponExpirationCheck({
    token: token || "",
    supplierId: supplierId || "",
    customerId: userId || "",
    discountCode: (liveDiscountDisplay.code || couponInput).trim(),
    products: couponProducts,
    onRemoveCoupon: () => {
      setPerItemCouponMap({});
      setCouponTouched(false);
      showSuccess("Expired coupon removed. Proceeding with original prices.");
    },
  });

  const [lastUpsert, setLastUpsert] = React.useState<any>(null);

  const initialFeeIds = React.useMemo<string[] | undefined>(() => {
    if (isEdit) {
      return getInitialSpecialFeeIdsFromOrder(editOrder);
    } else {
      const rows = cartHeader?.order_charges?.specialfee ?? [];
      const ids = Array.isArray(rows) ? rows.map((r: any) => String(r?.specialfee_id)).filter(Boolean) : [];
      // also consider backend's selected_fee_ids, if present
      const fromHeaderIds = Array.isArray(cartHeader?.selected_fee_ids) ? cartHeader.selected_fee_ids : [];
      const merged = Array.from(new Set([...(ids || []), ...fromHeaderIds.map(String)]));
      return merged.length ? merged : undefined;
    }
  }, [isEdit, editOrder, cartHeader]);

  const isShippingOptionsReady =
    !isEdit ||
    Boolean(editOrder?.id && taxAddressObj?.address_details?.addressLine1);

  const [initialShippingSelection, setInitialShippingSelection] =
    React.useState<InitialShippingSelection | null>(null);

  React.useEffect(() => {
    setInitialShippingSelection(null);
  }, [resumeOrderId]);

  React.useEffect(() => {
    if (!isEdit || !editOrder?.id) return;
    const selection = getInitialShippingSelectionFromOrder(editOrder);
    if (selection) setInitialShippingSelection(selection);
  }, [isEdit, editOrder?.id]);

  const savedShippingRowsForEdit = React.useMemo(
    () => (isEdit && editOrder ? mapOrderShippingRowsForPicker(editOrder) : null),
    [isEdit, editOrder?.id]
  );

  const savedSummary = React.useMemo(() => {
    if (isEdit) {
      const items = Array.isArray(editOrder?.items) ? editOrder.items : [];
      const sub = items.reduce((s: number, it: any) => s + Number(it.item_sub_total || 0), 0);
      const prodTax = items.reduce((s: number, it: any) =>
        s + Number(it?.item_tax_info?.total_product_tax_amount || it?.tax_amount || 0), 0);

      const shipRows = editOrder?.order_charges?.shipping_option ?? [];
      const feeRows = editOrder?.order_charges?.specialfee ?? [];
      const shipAmt = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_amount || 0), 0);
      const shipTax = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_tax_amount || 0), 0);
      const feeAmt = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_amount || 0), 0);
      const feeTax = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_tax_amount || 0), 0);

      const discount = Number(editOrder?.discount_amount || 0);
      const taxTotal = prodTax + shipTax + feeTax;

      return {
        subTotal: +sub.toFixed(2),
        discount: +discount.toFixed(2),
        shipping: +shipAmt.toFixed(2),
        special: +feeAmt.toFixed(2),
        tax: +taxTotal.toFixed(2),
        grand: +(sub + shipAmt + feeAmt + taxTotal).toFixed(2),
        discountCode: editOrder?.discount_code || "",
      };
    } else if (cartHeader) {
      // use server truth from cart
      const shipRows = cartHeader?.order_charges?.shipping_option ?? [];
      const feeRows = cartHeader?.order_charges?.specialfee ?? [];
      const shipAmt = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_amount || 0), 0);
      const shipTax = shipRows.reduce((s: number, r: any) => s + Number(r.shipping_tax_amount || 0), 0);
      const feeAmt = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_amount || 0), 0);
      const feeTax = feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_tax_amount || 0), 0);

      // prefer API-provided fields if present
      const sub = Number(cartHeader.subtotal_amount ?? 0);
      const discount = Number(cartHeader.discount_amount ?? 0);
      const tax = Number(cartHeader?.order_tax_summary?.over_all_tax_amount ?? (shipTax + feeTax)); // product tax may be 0 if manual
      const grand = Number(cartHeader.total_amount ?? (sub + shipAmt + feeAmt + tax));

      return {
        subTotal: +sub.toFixed(2),
        discount: +discount.toFixed(2),
        shipping: +shipAmt.toFixed(2),
        special: +feeAmt.toFixed(2),
        tax: +tax.toFixed(2),
        grand: +grand.toFixed(2),
        discountCode: cartHeader?.discount_code || "",
      };
    }
    return null;
  }, [isEdit, editOrder, cartHeader]);

  const hydratedRef = React.useRef(false);         // optional (for quick checks)
  const [hydrated, setHydrated] = React.useState(false);  // <- actual state we depend on

  const subTotalProductsBeforeDiscount = React.useMemo(() => {
    const rows = Array.isArray(cartItems) ? cartItems : [];
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const r2 = (n: number) => Math.round(n * 100) / 100;

    return r2(
      rows.reduce((sum, row) => {
        const qty = Math.max(0, num(row.quantity));
        // prefer unitPrice (if present in your rows), else unit_purchase_price,
        // then fall back to other known unit fields just in case.
        const unit =
          num((row as any).unitPrice) ||
          num(row.unit_purchase_price) ||
          num((row as any).unit_price) ||
          num(row.unit_retail_price);
        return sum + qty * unit;
      }, 0)
    );
  }, [cartItems]);



  return (
    <>
      <div className="teammember container-fluid container-1440">
        <div className="">
          <div className="col-lg-8">
            <h2 className="m-0  fw-bold addheaders mb-3">Checkout </h2>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-8 mb-4">
            <form onSubmit={formik.handleSubmit}>
              <div className="">
                <div className="box-shadow shadow-border-3 col-md-12 px-3">
                  <div className="">
                    <div className="row ms-2 mt-3">
                      <div id="row-overlap">
                        <h6 className="mb-0 d-flex align-items-center order-tittle ms-0 mt-3 mb-3">
                          <span className="icon-chip chip-blue me-2">
                            <i className="bi bi-card-checklist" />
                          </span>
                          <span>Order Details</span>
                        </h6>
                      </div>
                      <div className="col-md-6 mb-3" id="row-overlap">
                        <UIInputPro
                          formik={formik}
                          name="userFirstName"
                          label={
                            <>
                              First Name <span className="text-danger">*</span>
                            </>
                          }
                          placeholder="Enter first name"
                          value={formik.values.userFirstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>

                      {/* Last Name */}
                      <div className="col-md-6 mb-3" id="row-overlap">
                        <UIInputPro
                          formik={formik}
                          name="userLastName"
                          label={
                            <>
                              Last Name <span className="text-danger">*</span>
                            </>
                          }
                          placeholder="Enter last name"
                          value={formik.values.userLastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>

                      {/* Email */}
                      <div className="col-md-6 mb-3" id="row-overlap">
                        <UIInputPro
                          formik={formik}
                          name="userEmail"
                          label={<>Email <span className="text-danger">*</span></>}
                          type="email"
                          placeholder="Enter email"
                          value={formik.values.userEmail}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>

                      {/* Phone */}
                      <div className="col-md-6 mb-3" id="row-overlap">
                        <UIInputPro
                          formik={formik}
                          name="userPhone"
                          label={<>Phone <span className="text-danger">*</span></>}
                          placeholder="Enter phone"
                          value={formik.values.userPhone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                    </div>


                  </div>
                  <hr className="mt-3" />
                  <div className="my-2" id="row-overlap">
                    <h5 className="mb-4 table-headers">Billing Information</h5>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div className="align-items-center d-flex form-check gap-2 ps-0">
                        <input
                          type="checkbox"
                          id="isSameAsBilling"
                          name="isSameAsBilling"
                          // onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.isSameAsBilling}
                          onChange={(e) => {
                            formik.handleChange(e);
                            const isChecked = e.target.checked;

                            if (isChecked) {
                              const billingId = formik.values.billingAddressId;
                              formik.setFieldValue("shippingAddressId", billingId);
                              formik.setFieldValue("isSameAsBilling", true);
                              checkProceedEligibility(billingId, billingId);
                            } else {
                              formik.setFieldValue("shippingAddressId", "");
                              formik.setFieldValue("isSameAsBilling", false);
                              setSelectedShippingAddress(null);
                              checkProceedEligibility(
                                formik.values.billingAddressId,
                                ""
                              );
                            }
                          }}
                        // onBlur={formik.handleBlur}
                        // checked={formik.values.isSameAsBilling}
                        />
                        <label className="form-check-label table-cells" htmlFor="isSameAsBilling">
                          Is Billing and Shipping address the same
                        </label>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary  table-addbutton"
                        onClick={() => setShowAddressForm(true)}
                        style={{ whiteSpace: "nowrap" }}
                      // disabled={!!selectedBillingAddress}
                      >
                        + Address
                      </button>
                    </div>
                  </div>
                  <div className="row mb-2 mt-2">
                    <div className="col-md-6" id="row-overlap">
                      <div className="my-2 ms-3">
                        <UIReactSelectPro
                          formik={formik}
                          name="billingAddressId"
                          label="Select Billing Address"
                          options={addressOptions}
                          required
                          value={
                            addressOptions?.find(
                              (opt: any) => opt.value === formik.values.billingAddressId
                            ) || null
                          }
                          placeholder="- Select -"
                          onChange={(selected: any) => {
                            const billingId = selected?.value ?? "";
                            formik.setFieldValue("billingAddressId", billingId);
                            
                            setShowPaymentSection(false);

                            const selectedAddress = addressObjects?.find(
                              (addr: any) => addr._id === billingId
                            );
                            setSelectedBillingAddress(selectedAddress || null);
                            
                            // âœ… If "same as billing" is checked, also update shipping address
                            if (formik.values.isSameAsBilling) {
                              formik.setFieldValue("shippingAddressId", billingId);
                              setSelectedShippingAddress(selectedAddress || null);
                            }

                            // keep both args as ids (values)
                            const shippingId = formik.values.isSameAsBilling ? billingId : formik.values.shippingAddressId;
                            checkProceedEligibility(billingId, shippingId);
                          }}
                        />
                      </div>
                    </div>

                    {formik.values.isSameAsBilling === false && (
                      <div className="col-md-6 mt-3 mb-2" id="row-overlap">
                        <h5 className=" mt-2 table-headers"></h5>

                        <UIReactSelectPro
                          formik={formik}
                          name="shippingAddressId"
                          label="Select Shipping Address"
                          required
                          options={addressOptions}
                          value={
                            addressOptions?.find(
                              (opt: any) => opt.value === formik.values.shippingAddressId
                            ) || null
                          }
                          placeholder="- Select -"
                          onChange={(selected: any) => {
                            formik.setFieldValue("shippingAddressId", selected?.value ?? "");
                            setShowPaymentSection(false);

                            const selectedAddress = addressObjects?.find(
                              (addr: any) => addr._id === selected?.value
                            );
                            setSelectedShippingAddress(selectedAddress || null);

                            checkProceedEligibility(
                              formik.values.billingAddressId,
                              selected?.value ?? ""
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {/* <div className="row mb-3 rcust-margin-4">
                  <div className="col-md-12 mt-2 d-flex justify-content-between flex-column">
                    {shippingOptionError && (
                      <p className="text-danger mb-2">
                        Please select a shipping option before proceeding.
                      </p>
                    )}

                    <div className="d-flex justify-content-between">
                      <div></div>
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={!!order || !canProceedToPayment}
                        onClick={() => {
                          void createDraftOrderProcess();
                        }}
                      >
                        Draft Order
                      </button>
                    </div>
                  </div>
                </div> */}
                  <hr className="mt-3" />

                  <div className="card ms-1">
                    <div className="card-body">
                      <div className="table-responsive table-card rdt-desktop" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <div style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}>
                          <h6 className="mb-0 d-flex align-items-center order-tittle ms-1 mb-3">
                            <span
                              aria-hidden="true"
                              className="image-icons"
                              style={{
                                backgroundColor: "#E17100",
                                WebkitMaskImage: `url(${ProductSelection})`,
                                maskImage: `url(${ProductSelection})`,
                              }}
                            />
                            <span className="ms-2">Product Details</span>
                          </h6>

                          <table
                            className="table align-middle table-borderless mb-0 table-fixed checkout-product-table"
                            style={{ minWidth: "1100px", width: "100%" }}
                          >
                            <colgroup>
                              <col style={{ width: "26%" }} />
                              <col style={{ width: "9%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "9%" }} />
                              <col style={{ width: "10%" }} />
                              <col style={{ width: "8%" }} />
                              {/* <col style={{ width: "12%" }} /> */}
                              <col style={{ width: "15%" }} />
                            </colgroup>
                            <thead className="table-light text-center" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                              <tr className="table-light">
                                <th className="table-headers text-center" style={{ minWidth: "260px", whiteSpace: "normal", lineHeight: 1.25 }}>Product Details</th>
                                <th className="table-headers text-center" style={{ minWidth: "90px", whiteSpace: "normal", lineHeight: 1.25 }}>Sku</th>
                                <th className="table-headers text-center" style={{ minWidth: "100px", whiteSpace: "normal", lineHeight: 1.25 }} title="Unit Price">Item Price</th>
                                <th className="table-headers text-center" style={{ minWidth: "88px", whiteSpace: "normal", lineHeight: 1.25 }}>Quantity</th>
                                <th className="table-headers text-center" style={{ minWidth: "90px", whiteSpace: "normal", lineHeight: 1.25 }}>Discount</th>
                                {/* <th className="table-headers text-center" style={{ minWidth: "72px", whiteSpace: "normal", lineHeight: 1.25 }}>Tax</th> */}
                                <th className="table-headers text-center" style={{ minWidth: "110px", whiteSpace: "normal", lineHeight: 1.25 }}>Total Amount</th>
                                <th className="table-headers text-center" style={{ minWidth: "48px", whiteSpace: "normal" }} aria-hidden="true" />
                              </tr>
                            </thead>
                            <tbody>
                              {(cartItems.length === 0 ? [{}] : cartItems).map((item: any, index: number) => {

                                const num = (v: any) => Number(v || 0);
                                const r2 = (n: number) => Math.round(num(n) * 100) / 100;

                                const key = `${item.product_id}:${item.variant_id}`;
                                const qty = Math.max(1, num(item.quantity));
                                const unitRetail = num(item.unit_retail_price ?? item.unit_purchase_price);
                                const changedUnit = num(item.changed_unit_price ?? unitRetail);

                                const skuKey = String(item.sku || "");
                                const k = key; // for productTaxMap
                                // const qty = num(item.quantity);

                                // images
                                const firstId = idOf(item?.images?.[0]);
                                const src =
                                  (item?.design_id && item?.preview_url
                                    ? getImageSrc(item.preview_url, imageUrlMap) || buildImageUrl(item.preview_url)
                                    : "") ||
                                  getImageSrc(firstId, imageUrlMap) ||
                                  getImageSrc(item?.image_url, imageUrlMap) ||
                                  buildImageUrl(firstId || item?.image_url) ||
                                  "";

                                const srcOk =
                                  !!src &&
                                  src.trim() !== "" &&
                                  !src.endsWith("/undefined") &&
                                  !src.includes("/null") &&
                                  !src.endsWith("/");
                                const showProductImg = srcOk ? src : "";

                                // â€”â€”â€” server defaults (GET) â€”â€”â€”
                                const srvChangedEach = num(item.changed_unit_price ?? item.unit_retail_price);
                                const srvLineDisc = num(item.discount_on_total_quantity_amount);
                                const srvItemSub = num(item.item_sub_total ?? srvChangedEach * qty);
                                const srvRowTax = num(item?.item_tax_info?.total_product_tax_amount);
                                const srvRowTaxPct = num(item?.item_tax_info?.tax_percentage);

                                // â€”â€”â€” live from UI/engines â€”â€”â€”
                                // const liveSplit = perItemCouponMap?.[key]; // undefined if not changed by CouponDiscount yet
                                // const rowDiscount = (typeof liveSplit === "number")
                                //   ? num(liveSplit)
                                //   : srvLineDisc;

                                // original unit from UI/state (editable input)
                                const baseUnit = num(item.unit_retail_price);

                                // if live split exists, compute changed each from it; else keep server changed
                                // const changedEach = (typeof liveSplit === "number")
                                //   ? round2(Math.max(0, baseUnit * qty - rowDiscount) / Math.max(1, qty))
                                //   : srvChangedEach;
                                // safeConsole.log("changedEach", changedEach)

                                // const itemSub = (typeof liveSplit === "number")
                                //   ? round2(changedEach * qty)
                                //   : srvItemSub;

                                // row tax from engine (preferred) or server
                                // const liveRowTax = productTaxMap?.[k];
                                const liveRowTaxRaw =
                                  (skuKey && productTaxMap?.[skuKey] !== undefined ? productTaxMap[skuKey] : undefined) ??
                                  productTaxMap?.[k];
                                const liveRowTax = Number(liveRowTaxRaw ?? NaN);
                                const rowTax = (typeof liveRowTax === "number") ? num(liveRowTax) : srvRowTax;
                                const itemSub = r2(changedUnit * qty);
                                const rowTaxPct = itemSub > 0 ? (rowTax / itemSub) * 100 : srvRowTaxPct;
                                const { lineDiscount: rowDiscount, lineTotal: lineWithTax } =
                                  getAgentRealtorCheckoutLinePricing({
                                    item,
                                    qty,
                                    unitRetail,
                                    changedUnitPrice: changedUnit,
                                    lineKey: key,
                                    rowTax,
                                  });

                                // helpers


                                // server totals (from loaded order)
                                const srvSubProducts = num(order?.subtotal_amount);
                                const srvTotalAmount = num(order?.total_amount);
                                const srvDiscountTotal = num(order?.discount_amount);
                                const srvTaxOverall = num(order?.order_tax_summary?.over_all_tax_amount);

                                const srvShipRows = Array.isArray(order?.order_charges?.[0]?.shipping_option)
                                  ? order!.order_charges![0]!.shipping_option! : [];
                                const srvFeeRows = Array.isArray(order?.order_charges?.[0]?.specialfee)
                                  ? order!.order_charges![0]!.specialfee! : [];

                                const srvShipAmt = srvShipRows.reduce((s, r: any) => s + num(r.shipping_amount), 0);
                                const srvShipTax = srvShipRows.reduce((s, r: any) => s + num(r.shipping_tax_amount), 0);
                                const srvFeeAmt = srvFeeRows.reduce((s, r: any) => s + num(r.specialfee_amount), 0);
                                const srvFeeTax = srvFeeRows.reduce((s, r: any) => s + num(r.specialfee_tax_amount), 0);

                                // live charges from children
                                const liveShipAmt = num(shippingAmount);
                                const liveShipTax = num(shippingTaxAmount);
                                const liveFeeAmt = num(specialFeeAmount);
                                const liveFeeTax = num(specialFeeTaxAmount);

                                // live product totals from engine
                                const liveSubProducts = num(productTotals?.subTotal);
                                const liveProdTax = num(productTotals?.productTax);

                                // choose: prefer LIVE if engines/children produced anything, else SERVER
                                const usingLive = (liveSubProducts || liveShipAmt || liveFeeAmt || liveProdTax || liveShipTax || liveFeeTax) > 0;

                                const subTotalProducts = usingLive ? liveSubProducts : srvSubProducts;

                                const shippingAmountToShow = usingLive ? liveShipAmt : srvShipAmt;
                                const specialFeeAmountToShow = usingLive ? liveFeeAmt : srvFeeAmt;

                                const productTaxToShow = usingLive
                                  ? liveProdTax
                                  : num(srvTaxOverall - srvShipTax - srvFeeTax);

                                const shippingTaxToShow = usingLive ? liveShipTax : srvShipTax;
                                const feeTaxToShow = usingLive ? liveFeeTax : srvFeeTax;

                                const totalTaxAll = num(productTaxToShow) + num(shippingTaxToShow) + num(feeTaxToShow);

                                return (
                                  <tr key={index}>
                                    <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word", minWidth: "300px" }}>
                                      <div className="d-flex gap-3 align-items-start">
                                        {/* Product Image */}
                                        <div style={{ width: "80px", height: "100px", flexShrink: 0 }}>
                                          <img
                                            src={showProductImg || imagecustomRounded}
                                            alt=""
                                            className="img-fluid rounded"
                                            style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                            onError={(e) => {
                                              (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                            }}
                                          />
                                        </div>

                                        {/* Product Info */}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          {(() => {
                                            const signageIdForProductLink = String(
                                              signageId ||
                                                supplierId ||
                                                (typeof window !== "undefined" ? localStorage.getItem("signageId") : "") ||
                                                (item as any)?.supplier_id ||
                                                ""
                                            ).trim();
                                            const productIdForLink = String(item?.product_id ?? "").trim();
                                            const isVariableLine = String(item?.product_type ?? "").toLowerCase() === "variable";
                                            const signageProductDetailHref =
                                              signageIdForProductLink && productIdForLink
                                                ? isVariableLine
                                                  ? `/agent/viewSignageVariableProduct/${signageIdForProductLink}/${productIdForLink}`
                                                  : `/agent/viewSignageProduct/${signageIdForProductLink}/${productIdForLink}`
                                                : "";
                                            const title = item.name || item.product_name;
                                            return (
                                          <h5 className="fs-15 mb-1 wrap-anywhere">
                                            {signageProductDetailHref ? (
                                            <a
                                              href={signageProductDetailHref}
                                              className="link-primary wrap-anywhere"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {title}
                                            </a>
                                            ) : (
                                              <span className="wrap-anywhere">{title}</span>
                                            )}
                                          </h5>
                                            );
                                          })()}

                                          {/* Custom fields */}
                                          {(() => {
                                            const fields = item?.customizations?.fields || [];
                                            const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
                                            const firstFileVal = (raw: any): string => {
                                              if (!raw) return "";
                                              if (typeof raw === "string") return raw.trim();
                                              if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
                                              return "";
                                            };
                                            const hasValue = (f: any): boolean => {
                                              if (isFile(f)) return !!firstFileVal(f?.value);
                                              return String(f?.value ?? "").trim().length > 0;
                                            };
                                            if (!fields.some(hasValue)) return null;
                                            const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
                                            const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));

                                            return (
                                              <div className="custom-fields-container wrap-anywhere">
                                                {textFields.map((field: any, idx: number) => (
                                                  <div
                                                    key={`${field?._id || field?.field_name || "cf"}-t-${idx}`}
                                                    className="mb-2"
                                                    style={{ wordBreak: "break-word", maxWidth: "100%" }}
                                                  >
                                                    <strong className="custom-cells">{field.field_name} :</strong>{" "}
                                                    <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                                                  </div>
                                                ))}
                                                {fileFields.map((field: any, idx: number) => {
                                                  const id = firstFileVal(field?.value);
                                                  const href = getImageSrc(id, imageUrlMap) || buildImageUrl(id);
                                                  return (
                                                    <div
                                                      key={`${field?._id || field?.field_name || "cf"}-file-${idx}`}
                                                      className="mb-2"
                                                      style={{ wordBreak: "break-word", maxWidth: "100%" }}
                                                    >
                                                      <div className="mb-1">{field.field_name}</div>
                                                      {href ? (
                                                        <a href={href} download target="_blank" rel="noopener noreferrer">
                                                          <img
                                                            src={href}
                                                            alt={field.field_name}
                                                            className="rounded"
                                                            style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                          />
                                                        </a>
                                                      ) : /^[0-9a-f]{24}$/i.test(id) ? (
                                                        <span style={{ color: "#999" }}>Loadingâ€¦</span>
                                                      ) : null}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </td>

                                    {/* SKU */}
                                    <td style={{ minWidth: "100px" }}>
                                      <div className="fw-semibold text-center wrap-anywhere">
                                        {item?.sku || "-"}
                                      </div>
                                    </td>

                                      <td className="py-2 text-center align-middle" style={{ minWidth: "95px" }}>
                                      <div className="fw-semibold fs-6">${unitRetail.toFixed(2)}</div>
                                    </td>

                                    {/* Quantity */}
                                    <td className="py-2" style={{ minWidth: "90px" }}>
                                      <input
                                        type="number"
                                        className="form-control text-center"
                                        value={item?.quantity || 0}
                                        onChange={(e) => handleQuantityChange(index, e)}
                                        onKeyDown={(e) => {
                                          if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
                                        }}
                                        disabled
                                      />
                                    </td>

                                    {/* <td className="py-2 text-center align-middle" style={{ minWidth: "95px" }}>
                                      <div className="fw-semibold fs-6">${changedPrice.toFixed(2)}</div>
                                    </td> */}

                                    <td className="text-center align-middle" style={{ minWidth: "90px" }}>
                                      <DiscountAmountDisplay amount={rowDiscount} />
                                    </td>

                                    {/* Tax column */}
                                    {/* <td className="fw-semibold fs-6 text-center align-middle" style={{ minWidth: "80px" }}>
                                      ${num(rowTax ?? 0).toFixed(2)}
                                      <div className="fw-semibold fs-6">{(rowTaxPct ?? 0).toFixed(2)}%</div>
                                    </td> */}

                                    {/* Each (changed unit) + Total */}
                                    <td className="py-2 text-center align-middle" style={{ minWidth: "120px" }}>
                                      {/* <div className="text-muted">${changedEach.toFixed(2)} each</div> */}
                                      <div className="fw-semibold fs-6">${lineWithTax.toFixed(2)}</div>
                                    </td>

                                    {/* Row actions */}
                                    <td className="text-center" style={{ minWidth: "20px" }}>
                                      <div className="d-flex align-items-center justify-content-between gap-2">
                                        <div className="d-flex align-items-center gap-1">
                                          {/* {(cartItems.length === 0 || index === cartItems.length - 1) && (
                                            <button
                                              type="button"
                                              className="border-0 p-0"
                                              onClick={() => setPickerOpen(true)}
                                            >
                                              <i className="ri-add-line text-success fs-18"></i>
                                            </button>
                                          )} */}
                                          {cartItems.length > 1 && (
                                            <button
                                              type="button"
                                              className="border-0 p-0"
                                              onClick={() =>
                                                setCartItems(prev => prev.filter((_, i) => i !== index))
                                              }
                                            >
                                              <i className="ri-close-line text-danger fs-18"></i>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="rdt-mobile box-shadow-datatables shadow-border-2">
                        <MobileCardsOnly table={mobileTable} />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <InstructionsCreateCard
                        title="Instructions"
                        mode="instructions_files"
                        loginType="signage"
                        value={formik.values.notes}
                        onChange={(v) => formik.setFieldValue("notes", v, false)}  // live sync
                        onLocalInstructionFilesChange={handlePendingInstructionFilesChange}
                        placeholder="Add instructions here"
                        rows={4}
                        className="mt-3"
                        iconUrl={OrderNotes}
                        iconColor="#10B981"
                        iconBackground="#EAF7F1"
                      />
                    </div>
                    <div className="col-12 col-lg-6" id="row-overlap">
                      <div className="card box-shadow-datatables shadow-border-2">
                        <div className="ms-3 py-3">
                          <h6 className="mb-0 order-tittle">Order Totals</h6>
                        </div>
                        <div className="card-body">
                          {/* âœ… Always show CouponDiscount - it will auto-apply saved coupons or allow new ones */}
                          <OrderDiscountCodeField
                            className="mb-2"
                            value={couponInput}
                            appliedCode={liveDiscountDisplay.code}
                            discountAmount={liveDiscountDisplay.amount}
                            voucherApplied={
                              liveDiscountDisplay.amount > 0 &&
                              Boolean(liveDiscountDisplay.code.trim())
                            }
                            disabled={isRecomputingSales}
                            error={couponResolveError}
                            onChange={setCouponInput}
                            onApply={(code) => {
                              const trimmed = code.trim();
                              if (!trimmed) return;
                              userClearedCouponRef.current = false;
                              setCouponInput(trimmed);
                              setLiveDiscountDisplay({ code: "", amount: 0 });
                              setPerItemCouponMap({});
                              setCouponTouched(true);
                              void recomputeSalesRef.current?.({ force: true, discountCode: trimmed });
                            }}
                            onClear={() => {
                              setCouponInput("");
                              setCouponResolveError("");
                              userClearedCouponRef.current = true;
                              setCouponTouched(true);
                              setLiveDiscountDisplay({ code: "", amount: 0 });
                              setPerItemCouponMap({});
                              void recomputeSalesRef.current?.({ force: true, discountCode: "" });
                            }}
                          />
                          {zeroOrNegativeMsg && (
                            <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>
                          )}
                          
                          <hr />

                          {/* <div className="d-flex justify-content-between py-1">
                            <span className="order-card-title">Sub Total:</span>
                            <span className="order-card-cells">{money(savedSummary ? savedSummary.subTotal : subTotal)}</span>
                          </div> */}
                          {/* {couponAmount > 0 && ( */}
                          {/* <div className="d-flex justify-content-between py-1 text-success">
                            <span className="order-card-title ">Discount (coupon):</span>
                            <span className="order-card-cells">-{money(savedSummary ? savedSummary.discount : 0)}</span>
                          </div> */}
                          {/* )} */}
                          {/* <div className="d-flex justify-content-between py-1">
                            <span className="order-card-title">Shipping Charge:</span>
                            <span className="order-card-cells">
                              {money(shippingAmountToShow)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between py-1">
                            <span className="order-card-title">Special Fee:</span>
                            <span className="order-card-cells">{money(savedSummary ? savedSummary.special : liveSpecialFee)}</span>
                          </div>
                          <div className="py-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <button
                                type="button"
                                onClick={() => setShowTaxDetails(v => !v)}
                                className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                aria-expanded={showTaxDetails}
                                aria-controls="tax-breakdown"
                              > */}
                          {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                          {/* <span className="order-card-title">Tax :</span>
                              </button>
                              <span className="order-card-cells">
                                <span>{money(totalTaxAll)}</span>
                              </span>
                            </div> */}

                          {/* {showTaxDetails && (
                              <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                <div className="d-flex justify-content-between small mb-1">
                                  <span>Products Tax</span>
                                  <span className="order-card-cells">{money(productTaxToShow)}</span>
                                </div>
                                <div className="d-flex justify-content-between small mb-1">
                                  <span>Shipping Tax</span>
                                  <span className="order-card-cells">{money(shippingTaxToShow)}</span>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Special Fee Tax</span>
                                  <span className="order-card-cells">{money(feeTaxToShow)}</span>
                                </div>
                              </div>
                            )} */}
                          {/* </div> */}
                          {/* 
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between pt-1">
                            <strong className="order-card-title fs-19">Total (USD):</strong>
                            <span className="order-card-cells fw-semibold">{money(savedSummary ? savedSummary.grand : totalAmount)}</span>
                          </div> */}

                          {/* <div className="d-flex justify-content-between py-1">
                            <span className="order-card-title">Sub Total:</span>
                            <span className="order-card-cells">{money(subTotalProducts)}</span>
                          </div> */}
                          {/* âœ… SIMPLIFIED ORDER SUMMARY - Shows current UI state (matching RealtorOrderCheckout) */}
                          <div className="d-flex justify-content-between py-1">
                            <span className="order-card-title">Sub Total:</span>
                            <span className="order-card-cells">${uiSubtotal.toFixed(2)}</span>
                          </div>

                          {/* Discount (always show, even if $0) */}
                          {uiDiscount > 0 && liveDiscountDisplay.code ? (
                            <div className="d-flex justify-content-between py-1 text-success">
                              <span className="order-card-title" style={{ color: "#00a63e" }}>
                                Discount ({liveDiscountDisplay.code}):
                              </span>
                              <span className="order-card-cells" style={{ color: "#00a63e" }}>
                                -${uiDiscount.toFixed(2)}
                              </span>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-between py-1 text-success">
                              <span className="order-card-title" style={{ color: "#00a63e" }}>Discount (coupon):</span>
                              <span className="order-card-cells" style={{ color: "#00a63e" }}>-$0.00</span>
                            </div>
                          )}
                          
                          <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                            <span className="order-card-title text-break" style={{ maxWidth: "62%" }}>
                              {shippingChargeTotalsLabel}
                            </span>
                            <span className="order-card-cells flex-shrink-0">${uiShipping.toFixed(2)}</span>
                          </div>

                          <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                            <span
                              className="order-card-title text-break"
                              style={{ maxWidth: "62%" }}
                              title={specialFeeBreakdownDisplay.tooltip}
                            >
                              {specialFeeBreakdownDisplay.labelText}:
                            </span>
                            <span className="order-card-cells flex-shrink-0">${uiSpecialFee.toFixed(2)}</span>
                          </div>
                          
                          <div className="py-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="order-card-title">Tax:</span>
                              <span className="order-card-cells">${uiTotalTax.toFixed(2)}</span>
                            </div>

                            {/* {showTaxDetails && (
                              <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                <div className="d-flex justify-content-between small mb-1">
                                  <span>Products Tax</span>
                                  <span className="order-card-cells">{money(uiProductTax)}</span>
                                </div>
                                <div className="d-flex justify-content-between small mb-1">
                                  <span>Shipping Tax</span>
                                  <span className="order-card-cells">{money(uiShippingTax)}</span>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Special Fee Tax</span>
                                  <span className="order-card-cells">{money(uiSpecialFeeTax)}</span>
                                </div>
                              </div>
                            )} */}
                          </div>

                          <hr className="my-2" />
                          <div className="d-flex justify-content-between pt-1">
                            <strong className="order-card-title fs-19">Total (USD):</strong>
                            <span className="order-card-cells fw-semibold">${uiGrandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between align-items-center">
                      <button
                        className="btn back-btn surface-btn fw-bold fs-16"
                        type="button"
                        onClick={() => safeNavigate(-1)}
                      >
                        <i className="bi bi-chevron-left me-2" />
                        Back
                      </button>
                      <div className="d-flex justify-content-between">
                        <div></div>
                        <button
                          type="button"
                          className="btn prim-btnary save-btn surface-btn fw-bold fs-16"
                          disabled={!canSaveOrder || checkingCoupon}
                          // disabled={!canSaveOrder}
                          onClick={onConfirmAndPay}
                          title={
                            checkingCoupon
                              ? "Validating coupon..."
                              : !hasEmail
                                ? "Please enter an email"
                                : !hasPhone
                                  ? "Please enter a phone number"
                              :
                              !hasBilling
                                ? "Please select a billing address"
                                : !hasShipping
                                  ? "Please select a shipping address"
                                  : !hasShippingOption
                                    ? "Please select a shipping option"
                                    : isAnythingBusy
                                      ? "Processing..."
                                      : ""
                          }
                        >
                          {checkingCoupon ? "Validating Coupon..." : formSubmitting ? "Submitting..." : isAnythingBusy ? "Processing..." : "Process To Pay"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {showAddressForm && (
                <AddressFormModal
                  isOpen={true}
                  onClose={() => {
                    setShowAddressForm(false);
                  }}
                  token={token || ""}
                  owner={{
                    value: userId || "",
                    label: userName || "",
                  }}
                  useType="billing"
                   userType="agent"
                  prefill={{
                    label_of_address: formik.values.label_of_address || "",
                    firstName: formik.values.firstName || formik.values.userFirstName || userInfo?.firstName || "",
                    lastName: formik.values.lastName || formik.values.userLastName || userInfo?.lastName || "",
                    company: formik.values.company || "",
                    phone: formik.values.phone || formik.values.userPhone || userInfo?.phone1 || "",
                    addressLine1: formik.values.addressLine1 || "",
                    addressLine2: formik.values.addressLine2 || "",
                    city: formik.values.city || "",
                    state: formik.values.state || "",
                    zipcode: formik.values.zipcode || "",
                    country: formik.values.country || "",
                  }}
                  onCreated={(createdRaw, option, useType) => {
                    // setAddressObjects((prev) => [createdRaw, ...(prev || [])]);
                    setAddressObjects((prev) => [createdRaw, ...(prev || [])]);  // correct

                    formik.setFieldValue(
                      useType === "billing" ? "billingAddressId" : "shippingAddressId",
                      option.value
                    );

                    if (useType === "billing") {
                      setSelectedBillingAddress(createdRaw);
                    } else {
                      setSelectedShippingAddress(createdRaw);
                    }
                    if (useType === "billing" && formik.values.isSameAsBilling) {
                      formik.setFieldValue("shippingAddressId", option.value);
                      setSelectedShippingAddress(createdRaw);
                    }

                    setShowAddressForm(false);

                  }}
                  afterCreateRefresh={fetchAddresses}
                />
              )}
              {pickerOpen && (
                <ProductPickerModal
                  open={pickerOpen}
                  onClose={closePicker}
                  createdById={userId|| ""}
                  supplierId={supplierId}
                  onAdd={handleAddFromPicker}
                  studioBatchVariantPreviews={
                    Object.keys(pickerStudioBatchVariantPreviews).length
                      ? pickerStudioBatchVariantPreviews
                      : undefined
                  }
                />
              )}
            </form>
          </div>
          <div className="col-lg-4">
            <div className="card-body">
              <PONumber
                mode="create"
                disabled={Boolean(String(new URLSearchParams(location.search).get("project_id") || "").trim())}
                className="mb-3"
                value={String(formik.values.po_number || "")}
                onChange={(v) => formik.setFieldValue("po_number", v)}
              />
              {/* SHIPPING OPTIONS (UPS + Manual) */}
              {isShippingOptionsReady ? (
              <ShippingOptions
                key={
                  isEdit
                    ? `shipping-edit-${resumeOrderId}-${editOrder?.id ?? "pending"}-${shippingAddressSig}-${shippingProductsKey}`
                    : `shipping-create-${shippingProductsKey}`
                }
                ref={shipRef}
                token={token!}
                shippingAddress={taxAddressObj}
                totalQty={cartItems.reduce((s, r) => s + Number(r.quantity || 0), 0)}
                selectedProducts={selectedProductsWithDims}
                rateReloadKey={shippingProductsKey}
                productsFull={[]}
                createdById={userId || ""}
                supplierId={supplierId}
                taxStatus={editOrder?.order_tax_summary?.tax_status}
                initialSelectedIds={initialShippingSelection}
                savedShippingRows={savedShippingRowsForEdit}

                // de-duped state update (no render storms)
                onPayloadChange={handleShipPayloadChange}
                onBusyChange={handleShippingBusyChange}
              />
              ) : (
                <div className="card shadow-border-3 box-shadow">
                  <div className="card-body text-muted small">Loading shipping optionsâ€¦</div>
                </div>
              )}
            </div>

            {/* SPECIAL FEE PICKER */}
            <SpecialFeePicker
              key={isEdit ? `specialfee-edit-${resumeOrderId}-${editOrder?.id ?? "pending"}` : `specialfee-create-${userId}`}
              ref={feeRef}
              token={token!}
              personId={effectiveCreatedById} 
              supplierId={supplierId}
              shippingAddress={taxAddressObj}
              initialSelectedIds={initialFeeIds}
              taxStatus=""
              knownTaxType={activeTaxType} // child can decide how to compute; we still read rows/taxes
              isTaxable={true}
              statusOk={true}
              onPayloadChange={React.useCallback((p: FeePayload) => {
                feePayloadRef.current = p;
                setFeePayload(prev => {
                  const prevKey = JSON.stringify((prev?.specialfee ?? []).map(r => [r.specialfee_id, r.specialfee_amount, r.specialfee_tax_amount]));
                  const nextKey = JSON.stringify((p?.specialfee ?? []).map(r => [r.specialfee_id, r.specialfee_amount, r.specialfee_tax_amount]));
                  return (prevKey === nextKey) ? prev : p;
                });
                if (USE_SALES_ORDER_RESOLVE) {
                  if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
                  recomputeTimerRef.current = setTimeout(() => {
                    void recomputeSalesRef.current?.({ feePayload: p });
                  }, 300);
                }
              }, [])}
              onBusyChange={handleSpecialFeeBusyChange}
              onSourceFeesLoaded={(fees) => {
                specialFeeCatalogRef.current = fees;
              }}
            />

            {/* {taxError && (
              <div className="alert alert-danger py-2 px-3 mb-3">
                {taxError}
              </div>
            )} */}

            {showPaymentSection && (
              <>
                <div className="card border shadow-none">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-semibold mb-0">Payment</h6><span>${totalAmount}</span>
                    </div>
                    <hr />
                    {stripePromise && clientSecret && (
                      <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <div>
                          <h5 className="mb-1">Payment Selection</h5>
                          <p className="text-muted mb-4">Please select payment method</p>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-md-8">
                            <div className="form-check card-radio">
                              <input
                                id="paymentMethodOnline"
                                name="paymentMethod"
                                type="radio"
                                className="form-check-input"
                                checked={selectedPaymentMethod === "online"}
                                onChange={() => setSelectedPaymentMethod("online")}
                              />
                              <label className="form-check-label" htmlFor="paymentMethodOnline">
                                <i className="ri-bank-card-fill align-bottom"></i> <span>Online Payment</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {selectedPaymentMethod === "online" ? (
                          <div className="collapse show mt-4">
                            <div className="">
                              <PaymentForm
                                supplierId={supplierId}
                                totalAmount={totalAmount}
                                // redirectUrl="/agent/orders"
                                redirectUrl={`/agent/orderDetails/${order?.id}`}
                                dueAmount={0}
                                orderId={orderId}
                                selectedPaymentMethod={selectedPaymentMethod}
                                onPaymentSuccess={async () => {
                                  const paidOrderId = orderId || order?.id || "";
                                  if (token && paidOrderId) {
                                    try {
                                      const freshOrder =
                                        (await fetchSalesOrderForPaidFinalize(
                                          token,
                                          paidOrderId
                                        )) ?? (order as Record<string, unknown> | undefined);
                                      if (freshOrder) {
                                        await finalizeSalesOrderTaxAfterPaid({
                                          token,
                                          orderId: paidOrderId,
                                          order: freshOrder,
                                        });
                                      }
                                    } catch (finalizeErr) {
                                      console.warn(
                                        "[Checkout] Tax finalize after payment failed (non-blocking):",
                                        finalizeErr
                                      );
                                    }
                                  }
                                  setActiveTab("pills-finish-tab");
                                }}
                                userId={order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id || ""}
                                userEmail={order?.customer_email || ""}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-start gap-3 mt-4">
                            {/* <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => {
                                handleTabCompletion("pills-payment-tab");
                                setActiveTab("pills-finish-tab");
                                completeOrder(selectedPaymentMethod);
                              }}
                            >
                              Complete Order
                            </button> */}
                          </div>
                        )}
                      </Elements>
                    )}
                    {/* Move variable declaration outside TSX */}
                    {(() => {
                      const userIdForPayment = order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id;
                      return (
                        <PaymentMethods
                          orderId={order?.id || ""}
                          totalAmount={order?.total_amount || 0}
                          supplierId={order?.signage_id || ""}
                          id={userIdForPayment || ""}
                          ordertype="salesOrder"
                        />
                      );
                    })()}
                  </div>
                </div>
                {/* <div className="card h-40">
                    <div className="card-header">
                      <h5 className="card-title mb-0 table-headers">
                        <i className="ri-secure-payment-line align-bottom me-1 text-muted"></i> Payment Details
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-2">
                        <div className="flex-shrink-0">
                          <p className="text-muted mb-0 table-headers">Transaction ID:</p>
                        </div>
                        <div className="flex-grow-1 ms-2">
                          <h6 className="mb-0 table-cells">{order?.payment_info?.[0]?.payment_id || ""}</h6>
                        </div>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <div className="flex-shrink-0">
                          <p className="text-muted mb-0 table-headers">Payment Method:</p>
                        </div>
                        <div className="flex-grow-1 ms-2">
                          <h6 className="mb-0 table-cells">{order?.payment_info?.[0]?.method}</h6>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <p className="text-muted mb-0 table-headers">Total Amount:</p>
                        </div>
                        <div className="flex-grow-1 ms-2 table-cells">
                          <h6 className="mb-0">${order?.payment_info?.[0]?.total_amount}</h6>
                        </div>
                      </div>
                    </div>
                  </div> */}

              </>
            )}

          </div>
        </div>
      </div>

      <CheckoutPaymentModal
        open={checkoutPaymentOpen}
        onClose={() => setCheckoutPaymentOpen(false)}
        token={token || ""}
        variant="agent"
        order={checkoutPaymentOrder}
        totalAmount={Number(
          uiGrandTotal ?? checkoutPaymentOrder?.total_amount ?? totalAmount ?? 0
        )}
        supplierId={supplierId || ""}
        payerUserId={userId || ""}
        payerEmail={formik.values.userEmail || userInfo?.email || ""}
        paidById={userId || ""}
        paidByName={
          formik.values.userFirstName || formik.values.userLastName
            ? `${formik.values.userFirstName} ${formik.values.userLastName}`.trim()
            : userInfo?.firstName && userInfo?.lastName
              ? `${userInfo.firstName} ${userInfo.lastName}`
              : userName || ""
        }
        onPaymentComplete={(orderId) => {
          setCheckoutPaymentOpen(false);
          if (orderId) {
            navigate(`/agent/orderDetails/${orderId}`);
          }
        }}
        onClearCart={() => {
          clearCheckoutCartAfterPayment({
            dispatch,
            clearCartItems,
            fetchCart,
          });
        }}
      />
      
      {/* Coupon Expiration Modal */}
      {CouponExpirationModal}
    </>
  );
};

export default AgentOrderCheckout;










//  <table className="table table-nowrap align-middle table-borderless mb-0 table-fixed">
//                           <colgroup>
//                             <col style={{ width: "38%" }} />  {/* Product Details */}
//                             <col style={{ width: "12%" }} />  {/* Sku */}
//                             <col style={{ width: "10%" }} />  {/* Quantity */}
//                             <col style={{ width: "12%" }} />  {/* Item Price */}
//                             <col style={{ width: "10%" }} />  {/* Tax */}
//                             <col style={{ width: "10%" }} />  {/* Tax */}
//                             <col style={{ width: "18%" }} />  {/* Total Amount */}
//                             {/* <col style={{ width: "6%" }} />  */}
//                           </colgroup>
//                           <thead className="table-light text-center" style={{ position: "sticky", top: 0, zIndex: 1 }}>
//                             <tr>
//                               <th className="table-headers text-center">Product Details</th>
//                               <th className="table-headers text-center">Sku</th>
//                               <th className="table-headers text-center">Quantity</th>
//                               <th className="table-headers text-center">Item Price</th>
//                               <th className="table-headers text-center">Discount</th>
//                               <th className="table-headers text-center">Tax</th>
//                               <th className="table-headers text-center">Total Amount</th>
//                               {/* <th className="text-center table-headers"></th> */}

//                             </tr>
//                           </thead>
//                           <tbody>
//                             {cartItems.length > 0 ? (
//                               cartItems.map((item: CartItem, index: number) => {
//                                 // pick first usable id from item.images (string | {url})
//                                 const firstId = idOf(item?.images?.[0]);

//                                 // resolve via presigned map first, then fallback to BASE_URL
//                                 const src =
//                                   getImageSrc(firstId, imageUrlMap) ||
//                                   getImageSrc(item?.image_url, imageUrlMap) ||
//                                   buildImageUrl(firstId || item?.image_url);

//                                 // custom fields come from the row item (not product)
//                                 const fields = item?.customizations?.fields || [];
//                                 const hasAtLeastOneValue = fields.some(
//                                   (field: any) => field?.value && field.value.toString().trim() !== ""
//                                 );

//                                 const key = `${item.product_id}:${item.variant_id}`;
//                                 const each = effectiveMapdata[key] ?? Number(item.unit_purchase_price || 0);  // changed unit price
//                                 safeConsole.log("eachedit", each);
//                                 const lineTotal = each * Number(item.quantity || 0);
//                                 const rowKey = key;
//                                 safeConsole.log("rowKey", rowKey);
//                                 const rowDiscount = Number(perItemCouponMap[rowKey] || 0);
//                                 safeConsole.log("rowDiscount", rowDiscount);
//                                 const k = keyForRow({ productId: item.product_id, variantId: item.variant_id });
//                                 const qtyNum = Number(item.quantity || 0);
//                                 const rowTax = Number(productTaxMap[k] ?? 0);              // tax dollars for this row
//                                 const rowTaxPct = Number(productTaxMap[k] ?? 0);        // tax rate percent for this row
//                                 const lineNet = Number(each) * qtyNum;                     // changed-unit Ã— qty (after discount)
//                                 safeConsole.log("lineNet", lineNet);
//                                 const lineGross = lineNet + rowTax;
//                                 safeConsole.log("lineGross", lineGross);
//                                 const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);


//                                 return (
//                                   <tr key={index}>
//                                     <td
//                                       className="table-cells align-middle"
//                                       style={{ whiteSpace: "normal", wordWrap: "break-word" }}
//                                     >
//                                       <div className="d-flex gap-3 align-items-start">
//                                         <div style={{ width: "80px", height: "80px", flexShrink: 0 }}>
//                                           {src ? (
//                                             <img
//                                               src={src}
//                                               alt={item?.name || item?.product_name || ""}
//                                               className="img-fluid rounded border"
//                                               style={{ objectFit: "contain", width: "100%", height: "100%" }}
//                                             />
//                                           ) : (
//                                             <div
//                                               className="bg-light border rounded d-flex align-items-center justify-content-center"
//                                               style={{ width: "100%", height: "100%", fontSize: 12 }}
//                                             >
//                                               No Image
//                                             </div>
//                                           )}
//                                         </div>

//                                         <div>
//                                           <h5 className="fs-15 mb-1 wrap-anywhere">
//                                             <a
//                                               href={`/agent/viewVariableProduct/${item.product_id}`}
//                                               className="link-primary wrap-anywhere"
//                                             >
//                                               {item.name || item.product_name}
//                                             </a>
//                                           </h5>

//                                           {/* custom fields (view only) */}
//                                           {hasAtLeastOneValue && (
//                                             <div className="custom-fields-container">
//                                               {fields.map((field: any, idx: number) => (
//                                                 <div
//                                                   key={idx}
//                                                   className="mb-2"
//                                                   style={{ wordBreak: "break-word", maxWidth: 400 }}
//                                                 >
//                                                   {field.field_type === "file" ? (
//                                                     field.value && field.value.trim() !== "" ? (
//                                                       (() => {
//                                                         const id = (field?.value || "").toString().trim();
//                                                         const href =
//                                                           getImageSrc(id, imageUrlMap) || buildImageUrl(id);

//                                                         return (
//                                                           <>
//                                                             <div className="mb-1">{field.field_name}</div>
//                                                             <a
//                                                               href={href}
//                                                               download
//                                                               target="_blank"
//                                                               rel="noopener noreferrer"
//                                                             >
//                                                               <img
//                                                                 src={href}
//                                                                 alt={field.field_name}
//                                                                 className="rounded"
//                                                                 style={{
//                                                                   maxWidth: 100,
//                                                                   height: "auto",
//                                                                   cursor: "pointer",
//                                                                 }}
//                                                               />
//                                                             </a>
//                                                           </>
//                                                         );
//                                                       })()
//                                                     ) : (
//                                                       <div>
//                                                         <strong className="custom-cells">
//                                                           {field.field_name} :
//                                                         </strong>{" "}
//                                                         <span style={{ color: "#999", fontStyle: "italic" }}>
//                                                           --
//                                                         </span>
//                                                       </div>
//                                                     )
//                                                   ) : (
//                                                     <div>
//                                                       <strong className="custom-cells">
//                                                         {field.field_name} :
//                                                       </strong>{" "}
//                                                       {field.value?.toString().trim() || (
//                                                         <span style={{ color: "#999", fontStyle: "italic" }}>
//                                                           ---
//                                                         </span>
//                                                       )}
//                                                     </div>
//                                                   )}
//                                                 </div>
//                                               ))}
//                                             </div>
//                                           )}
//                                         </div>
//                                       </div>
//                                     </td>

//                                     <td className="table-cells text-center wrap-anywhere">{item.sku}</td>
//                                     {/* <td className="table-cells text-center wrap-anywhere">
//                                       ${Number(item.quantity || 0).toFixed(2)}
//                                     </td> */}

//                                     <td className="table-cells text-center wrap-anywhere">
//                                       <input
//                                         type="number"
//                                         className="form-control text-center"
//                                         min={1}
//                                         value={item.quantity || 1}
//                                         onChange={(e) => handleQuantityChange(index, e)}
//                                         disabled
//                                       />
//                                     </td>
//                                     <td className="table-cells text-center wrap-anywhere">
//                                       ${Number(item.unit_retail_price || 0).toFixed(2)}
//                                     </td>
//                                     <td className="text-success text-center align-middle">
//                                       {(() => {
//                                         const key = `${item.product_id}:${item.variant_id}`;
//                                         const qty = num(item.quantity);
//                                         const base = num(item.unit_purchase_price) * qty; // row's own base
//                                         let rowDisc = 0;

//                                         const orderPct = (couponDiscounts || []).find(d => d.scope === "order" && d.type === "percent");
//                                         const orderAmt = (couponDiscounts || []).find(d => d.scope === "order" && d.type === "amount");

//                                         if (orderPct) {
//                                           // % of THIS row only
//                                           rowDisc = round2(base * num(orderPct.value) / 100);
//                                         } else if (orderAmt) {
//                                           // fixed amount uses the precomputed split from CouponDiscount (constant per row)
//                                           rowDisc = num(perItemCouponMap?.[key]);
//                                         }

//                                         return rowDisc > 0 ? `-${money(rowDisc)}` : "â€“";
//                                       })()}
//                                     </td>

//                                     <td className="table-cells text-center wrap-anywhere">
//                                       {(() => {
//                                         const key = `${item.product_id}:${item.variant_id}`;
//                                         const live =
//                                           (productTaxMap instanceof Map)
//                                             ? productTaxMap.get(key)
//                                             : productTaxMap?.[key];

//                                         // live can be a number or an object with .amount; keep it minimal:
//                                         const liveAmt = typeof live === 'number' ? live : Number(live?.tax_amount || 0);
//                                         const savedAmt = Number(
//                                           (Array.isArray(item?.item_tax_info) ? item.item_tax_info[0]?.total_product_tax_amount : item?.item_tax_info?.total_product_tax_amount) || 0
//                                         );
//                                         const amount = Number(liveAmt || savedAmt || 0);

//                                         if (amount <= 0) return <span>--</span>;

//                                         // (optional) show %: amount / line base
//                                         const base = Number(item.unit_retail_price || 0) * Number(item.quantity || 0);
//                                         const pct = base > 0 ? (amount / base) * 100 : 0;

//                                         return (
//                                           <>
//                                             ${amount.toFixed(2)}
//                                             <div className="text-muted small wrap-anywhere">{pct.toFixed(2)}%</div>
//                                           </>
//                                         );
//                                       })()}
//                                     </td>

//                                     {/* <td className="table-cells text-center wrap-anywhere py-2">
//                                       ${(
//                                         Number(item.quantity || 0) * Number(item.unit_retail_price || 0) +
//                                         Number(item?.tax_details?.tax_amount ?? 0)
//                                       ).toFixed(2)}
//                                     </td> */}
//                                     <td className="py-2 text-center align-middle">
//                                       <div className="text-muted">${each.toFixed(2)} each</div>
//                                       <div className="fw-semibold fs-6">${lineGross.toFixed(2)}</div>
//                                     </td>

//                                     {/* <td className="text-center" style={{ minWidth: 48 }}>
//                                       <div className="d-flex align-items-center justify-content-center gap-2">
//                                         {index === cartItems.length - 1 && (
//                                           <button type="button" className="btn btn-link p-0" onClick={() => setPickerOpen(true)} title="Add">
//                                             <i className="ri-add-line text-success fs-18"></i>
//                                           </button>
//                                         )}
//                                         {cartItems.length > 1 && (
//                                           <button type="button" className="btn btn-link p-0" onClick={() => removeProductRow(index)} title="Remove">
//                                             <i className="ri-close-line text-danger fs-18"></i>
//                                           </button>
//                                         )}
//                                       </div>
//                                     </td> */}
//                                   </tr>
//                                 );
//                               })
//                             ) : (
//                               <tr>
//                                 <td className="text-center" colSpan={5}>
//                                   No items in cart.
//                                 </td>
//                               </tr>
//                             )}
//                           </tbody>


//                         </table>
